import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import BulkProgressPanel from '../components/BulkProgressPanel';
import { useDispatch } from 'react-redux';
import { getAlicuotaPercent, shouldDiscriminateIVA } from '../utils/tax';
import http from '../utils/request';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { getCategories, categoryCreate, categoryUpdate, categoryDelete } from '../features/category/categorySlice';
import { useModalEscape } from '../utils/useModalEscape';
import * as XLSX from 'xlsx';
import LabelPrinterModal from '../components/LabelPrinterModal';
import useSubscriptionRestrictions from '../hooks/useSubscriptionRestrictions';
import BarcodeScannerHtml5Qrcode from '../components/BarcodeScannerHtml5Qrcode';

// Usar rutas relativas para beneficiarnos del baseURL y el fallback automático del cliente http
const ENDPOINTS = {
  all: '/product/all-products',
  export: '/product/export',
  import: '/product/import',
  add: '/product/add-product',
  update: '/product/update-product',
  delete: (id) => `/product/delete/${id}`,
};

// Utilidad: redondear a 2 decimales (disponible a nivel de módulo para helpers externos)
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const Products = () => {
  const { user } = useSelector((state) => state.auth);
  const { fiscalStatus, fiscalConfig } = useSelector((state) => state.fiscal || {});
  const { tenant } = useSelector((state) => state.tenant);
  const { categories } = useSelector((state) => state.category || { categories: [] });
  const { daysOverdue } = useSubscriptionRestrictions();
  const isSubscriptionExpired = daysOverdue > 0;
  // Prefer full fiscalConfig (contains tipoResponsable/condicion) over the lightweight fiscalStatus
  const fiscal = fiscalConfig || fiscalStatus || {};
  const discriminarIVA = shouldDiscriminateIVA(fiscal);

  // Check if tenant is Responsable Inscripto (RI)
  const isResponsableInscripto = (() => {
    try {
      const tipoResp = fiscal?.tipoResponsable;
      const condIVA = fiscal?.condicionIVA ?? fiscal?.condicion;
      // Check by string or numeric code (1 = Responsable Inscripto)
      if (tipoResp && typeof tipoResp === 'string' && /responsable inscripto/i.test(tipoResp)) return true;
      if (condIVA != null && Number(condIVA) === 1) return true;
      return false;
    } catch (e) { return false; }
  })();

  // Show final price with IVA only for RI tenants
  const showFinalPriceWithIVA = isResponsableInscripto;
  const serviceType = tenant?.serviceType || user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';
  const isRestaurant = serviceType === 'restaurant';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterOnline, setFilterOnline] = useState(false); // 'asc' or 'desc'
  const [showCostMargin, setShowCostMargin] = useState(false); // Toggle para mostrar/ocultar Costo y Margen (por defecto oculto)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef(null);
  const nameInputRef = useRef(null); // Ref para autofocus en el campo nombre

  // Debounce the search input to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400); // 400ms delay
    return () => clearTimeout(timer);
  }, [search]);

  const focusSearch = useCallback(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showPhotoMode, setShowPhotoMode] = useState(false);
  const [photoModeBarcode, setPhotoModeBarcode] = useState('');
  const [photoModeProduct, setPhotoModeProduct] = useState(null);
  const [photoModeLoading, setPhotoModeLoading] = useState(false);
  const photoModeInputRef = useRef(null);
  const photoModeFileRef = useRef(null);
  const [isScanningPhotoCode, setIsScanningPhotoCode] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
  const [categoryEdits, setCategoryEdits] = useState({}); // Para manejar ediciones de categorías
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [newSupplierData, setNewSupplierData] = useState({ nombre: '', docNro: '', email: '', telefono: '' });
  const [createSupplierLoading, setCreateSupplierLoading] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkPctCost, setBulkPctCost] = useState('');
  const [bulkPctMargin, setBulkPctMargin] = useState('');
  const [bulkPctSale, setBulkPctSale] = useState('');
  const [bulkNoModifyStock, setBulkNoModifyStock] = useState(false);
  const [bulkEcommerceAction, setBulkEcommerceAction] = useState(''); // '' | 'enable' | 'disable'
  const [bulkIVACode, setBulkIVACode] = useState(''); // Código de IVA para ajuste masivo
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkCancelRequested, setBulkCancelRequested] = useState(false);
  const [activeBulkJob, setActiveBulkJob] = useState(null);

  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [printQueueIds, setPrintQueueIds] = useState(() => {
    try {
      const saved = localStorage.getItem('printQueueIds');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });
  const [viewingQueue, setViewingQueue] = useState(false); // true if modal opened from queue button
  const [isManualCategory, setIsManualCategory] = useState(false);
  const [componentSearchTexts, setComponentSearchTexts] = useState({}); // {componentIndex: searchText}

  const [editMode, setEditMode] = useState(false);
  const [removeBgProduct, setRemoveBgProduct] = useState(false); // Checkbox quitar fondo producto
  const [showQuickStockModal, setShowQuickStockModal] = useState(false);
  const [quickStockData, setQuickStockData] = useState({ barcode: '', stockToAdd: '', costPrice: '', salePrice: '', margin: '', priceWithIVA: false, product: null });
  const quickStockInputRef = useRef(null);

  const [error, setError] = useState('');
  const [showDeleteByCategoryModal, setShowDeleteByCategoryModal] = useState(false);
  const [deleteCategoryName, setDeleteCategoryName] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [importErrors, setImportErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLoaded, setUploadLoaded] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('idle'); // 'idle' | 'uploading' | 'processing' | 'done'
  
  // 📸 Productos sin fotos locales para el Modo Foto
  const pendingPhotosList = useMemo(() => {
    return products.filter(p => {
      const all = [p.image, ...(p.ecommerceImages || [])].filter(img => img && !img.startsWith('http'));
      return all.length === 0;
    });
  }, [products]);
  const abortCtrlRef = useRef(null);
  const processingTimerRef = useRef(null);
  const processingStartRef = useRef(null);
  const progressSamplesRef = useRef([]); // {t, p}
  const bulkCancelRequestedRef = useRef(false);
  const bulkJobControlRef = useRef({});
  const [processingEta, setProcessingEta] = useState(null); // seconds
  const MAX_PROCESSING_PCT = 95; // do not show 99% — keep some headroom until server finishes
  const getDisplayProgress = () => (uploadProgress >= MAX_PROCESSING_PCT ? MAX_PROCESSING_PCT : uploadProgress);
  // throttle refs to avoid too-frequent setState from onUploadProgress
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const lastProgressAtRef = useRef(0);
  const lastProgressPctRef = useRef(-1);

  const [formData, setFormData] = useState({
    _id: '',
    name: '',
    category: '',
    stock: '',
    costPrice: '',
    margin: '',
    salePrice: '',
    barcode: '',
    alicuotaIVACode: discriminarIVA ? 5 : 0, // 5 = 21% IVA por defecto para RI
    noControlStock: false,
    // Discount fields (UI only for now)
    discountType: 'none', // 'none' | 'percent' | 'fixed' | 'tiered'
    discountValue: 0, // percent or fixed amount
    discountTiers: [], // [{minQty, maxQty?, type: 'percent'|'fixed', value}]
    // Combo fields (UI removed) kept for compatibility
    isCombo: false,
    components: [], // [{ productId, qty, priceOverride }]
    comboPrice: '',
    variants: [], // [{ sku, attributes: { color, size }, stock, price }]
    // E-commerce fields
    sellOnline: false,
    ecommerceDescription: '',
    ecommerceImages: [],
    // Supplier
    supplierId: '',
  });

  // Búsqueda externa (Mercado Libre)
  const [showSearchExternalModal, setShowSearchExternalModal] = useState(false);
  const [searchExternalQuery, setSearchExternalQuery] = useState('');
  const [externalResults, setExternalResults] = useState([]);
  const [isSearchingExternal, setIsSearchingExternal] = useState(false);
  const [showPreviewLightbox, setShowPreviewLightbox] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const handleSearchExternal = async (queryOverride) => {
    const q = (typeof queryOverride === 'string' ? queryOverride : searchExternalQuery).trim() || formData.name;
    if (!q) {
      toast.warn('Ingresa un término de búsqueda');
      return;
    }
    try {
      setIsSearchingExternal(true);
      const res = await http.get(`/product/search-web?q=${encodeURIComponent(q)}`);
      setExternalResults(Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(res.data) && res.data.length === 0) toast.info('No se encontraron resultados');
    } catch (err) {
      console.error(err);
      toast.error('Error al buscar en la web');
    } finally {
      setIsSearchingExternal(false);
    }
  };

  // Variantes UI state
  const [variantMatrix, setVariantMatrix] = useState({ colors: '', sizes: '' });

  const handleGenerateVariants = () => {
    const colors = variantMatrix.colors.split(',').map(s => s.trim()).filter(Boolean);
    const sizes = variantMatrix.sizes.split(',').map(s => s.trim()).filter(Boolean);

    if (colors.length === 0 && sizes.length === 0) {
      toast.warning('Ingresa al menos un color o un talle');
      return;
    }

    const newVariants = [];
    const baseSku = formData.barcode || '';
    const basePrice = formData.salePrice || 0;

    if (colors.length > 0 && sizes.length > 0) {
      colors.forEach(c => {
        sizes.forEach(s => {
          newVariants.push({
            attributes: { color: c, size: s },
            stock: 0,
            sku: baseSku ? `${baseSku}-${c.substring(0, 3).toUpperCase()}-${s}` : '',
            price: basePrice
          });
        });
      });
    } else if (colors.length > 0) {
      colors.forEach(c => newVariants.push({
        attributes: { color: c },
        stock: 0,
        sku: baseSku ? `${baseSku}-${c.substring(0, 3).toUpperCase()}` : '',
        price: basePrice
      }));
    } else if (sizes.length > 0) {
      sizes.forEach(s => newVariants.push({
        attributes: { size: s },
        stock: 0,
        sku: baseSku ? `${baseSku}-${s}` : '',
        price: basePrice
      }));
    }

    setFormData(prev => ({
      ...prev,
      variants: [...(prev.variants || []), ...newVariants]
    }));
    toast.success(`${newVariants.length} variantes generadas`);
  };


  const handleVariantChange = (index, field, value) => {
    setFormData(prev => {
      const vars = [...(prev.variants || [])];
      // Si es atributo anidado
      if (field === 'color' || field === 'size') {
        vars[index] = {
          ...vars[index],
          attributes: { ...vars[index].attributes, [field]: value }
        };
      } else {
        vars[index] = { ...vars[index], [field]: value };
      }
      return { ...prev, variants: vars };
    });
  };

  const handleRemoveVariant = (index) => {
    setFormData(prev => {
      const vars = [...(prev.variants || [])];
      vars.splice(index, 1);
      return { ...prev, variants: vars };
    });
  };
  // Temporary inputs for adding a component to a combo (UI removed)
  // (state removed)
  const fileInputRef = useRef(null);

  // Responsive helper: detect small screens to make drawer full-width on mobile
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // F3 and ESC Key listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F3') {
        e.preventDefault();
        setQuickStockData({ barcode: '', stockToAdd: '', costPrice: '', product: null });
        setShowQuickStockModal(true);
      }
      if (e.key === 'Escape') {
        // Solo limpiar búsqueda si no hay ningún modal abierto (useModalEscape se encarga de cerrarlos)
        const anyModalOpen = showModal || showBulkModal || showCategoryModal || showQuickStockModal || showDeleteByCategoryModal || (importErrors && importErrors.length > 0);
        if (!anyModalOpen) {
          setSearch('');
          if (searchInputRef.current) searchInputRef.current.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, showBulkModal, showCategoryModal, showQuickStockModal, showDeleteByCategoryModal, importErrors]);

  useEffect(() => {
    if (showQuickStockModal && quickStockInputRef.current) {
      quickStockInputRef.current.focus();
    }
  }, [showQuickStockModal]);

  // Autofocus en el campo nombre cuando se abre el modal de producto
  useEffect(() => {
    if (showModal && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100); // Pequeño delay para asegurar que el modal esté renderizado
    }
  }, [showModal]);

  // Autofocus inicial en cargar la página
  useEffect(() => {
    focusSearch();
  }, [focusSearch]);

  // ESC key handler for modals
  useModalEscape(showModal, () => setShowModal(false));
  useModalEscape(showBulkModal, () => setShowBulkModal(false));
  useModalEscape(showCategoryModal, () => setShowCategoryModal(false));
  useModalEscape(showQuickStockModal, () => setShowQuickStockModal(false));
  useModalEscape(showDeleteByCategoryModal, () => setShowDeleteByCategoryModal(false));

  // 🔹 Traer productos
  // Helper to ensure image URLs are absolute when needed
  const resolveImageUrl = useCallback((url, width = 100) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    
    // Si es una imagen local, usamos el optimizador (y de paso limpiamos el prefijo /api si lo tiene)
    let filename = url;
    if (filename.startsWith('/api/uploads/')) filename = filename.replace('/api/uploads/', '');
    else if (filename.startsWith('/uploads/')) filename = filename.replace('/uploads/', '');
    
    return `/api/public/image/${filename}?w=${width}&q=80`;
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await http.get(ENDPOINTS.all);
      const data = Array.isArray(res.data) ? res.data : [];
      setProducts(data);
      setFiltered(data);
    } catch (err) {
      console.error(err.response?.data || err.message);
      setError('❌ Error al cargar productos');
    }
  }, []);

  const handleDeleteAllProducts = async () => {
    const isConfirm1 = window.confirm(
      "⚠️ ¡ADENCIÓN CRÍTICA! Esta acción eliminará permanentemente TODOS los productos de tu comercio de forma irreversible.\n\n¿Estás seguro de que deseas continuar?"
    );
    if (!isConfirm1) return;

    const confirmWord = window.prompt(
      "Para continuar y confirmar la eliminación masiva de todos los productos, escribe la palabra 'ELIMINAR' (en mayúsculas y sin comillas):"
    );
    
    if (confirmWord !== 'ELIMINAR') {
      toast.info('Eliminación masiva cancelada');
      return;
    }

    const deleteToast = toast.loading('Eliminando todos los productos...');
    try {
      const resp = await http.delete('/product/dev-delete-all');
      if (resp.data.success) {
        toast.update(deleteToast, {
          render: `✅ Se eliminaron ${resp.data.deletedCount} productos correctamente.`,
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
        await fetchProducts();
        setShowBulkModal(false);
      } else {
        toast.update(deleteToast, {
          render: 'Error al procesar la eliminación masiva.',
          type: 'error',
          isLoading: false,
          autoClose: 3000
        });
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Error de red o de permisos al eliminar productos.';
      toast.update(deleteToast, {
        render: `❌ ${msg}`,
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    }
  };

  useEffect(() => {
    if (user) fetchProducts();
    // fetch categories for dropdown
    try { dispatch(getCategories()); } catch (e) { }
  }, [user, fetchProducts, dispatch]);

  // Crear categoría desde el modal rápido
  const handleCreateCategory = async () => {
    const name = (newCategoryName || '').trim().toUpperCase();
    if (!name) {
      toast.warn('El nombre de la categoría no puede estar vacío');
      return;
    }
    try {
      setCreateCategoryLoading(true);
      // usar el thunk y esperar unwrap para capturar errores
      await dispatch(categoryCreate({ category: name })).unwrap();
      // refrescar categorías disponibles
      await dispatch(getCategories()).unwrap();
      toast.success('✅ Categoría creada');
      setNewCategoryName('');
      setShowCategoryModal(false);
      focusSearch();
    } catch (err) {
      const msg = (err && err.message) ? err.message : (err && err.data && err.data.message) ? err.data.message : 'Error creando categoría';
      console.error('create category error', err);
      toast.error(msg);
    } finally {
      setCreateCategoryLoading(false);
    }
  };

  // Cargar proveedores
  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await http.get('/suppliers?limit=100');
      setSuppliers(res.data.items || []);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      // No mostrar error al usuario, solo log
    }
  }, []);

  // Crear proveedor desde el modal rápido
  const handleCreateSupplier = async () => {
    const nombre = (newSupplierData.nombre || '').trim();
    if (!nombre) {
      toast.warn('El nombre del proveedor no puede estar vacío');
      return;
    }
    try {
      setCreateSupplierLoading(true);
      const res = await http.post('/suppliers', newSupplierData);
      await fetchSuppliers(); // Recargar lista
      toast.success('✅ Proveedor creado');
      // Asignar automáticamente el nuevo proveedor al producto
      setFormData(prev => ({ ...prev, supplierId: res.data._id }));
      setNewSupplierData({ nombre: '', docNro: '', email: '', telefono: '' });
      setShowSupplierModal(false);
      focusSearch();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Error creando proveedor';
      console.error('create supplier error', err);
      toast.error(msg);
    } finally {
      setCreateSupplierLoading(false);
    }
  };

  // --- Client-side bulk job persistence and runner (Option 1)
  const JOB_KEY_PREFIX = 'bulkJob:';
  const LATEST_JOB_KEY = 'bulkJob:latest';

  // Persist per-product local override for 'no control stock' so sales UI can allow selling
  // even if backend is temporarily unavailable or the product list is out-of-sync.
  const setLocalNoControlForIds = (ids = [], value = false) => {
    try {
      if (!Array.isArray(ids)) return;
      ids.forEach((id) => {
        if (!id) return;
        try { localStorage.setItem(`localNoControl:${id}`, value ? 'true' : 'false'); } catch (e) { }
      });
    } catch (e) { }
  };

  const removeLocalNoControlForIds = (ids = []) => {
    try {
      if (!Array.isArray(ids)) return;
      ids.forEach((id) => { if (!id) return; try { localStorage.removeItem(`localNoControl:${id}`); } catch (e) { } });
    } catch (e) { }
  };

  const persistJob = (job) => {
    try {
      localStorage.setItem(JOB_KEY_PREFIX + job.id, JSON.stringify(job));
      localStorage.setItem(LATEST_JOB_KEY, job.id);
    } catch (_) { }
  };

  const removeJob = (id) => {
    try {
      localStorage.removeItem(JOB_KEY_PREFIX + id);
      const latest = localStorage.getItem(LATEST_JOB_KEY);
      if (latest === id) localStorage.removeItem(LATEST_JOB_KEY);
    } catch (_) { }
  };

  useEffect(() => {
    // load latest job if any
    try {
      const latest = localStorage.getItem(LATEST_JOB_KEY);
      if (latest) {
        const raw = localStorage.getItem(JOB_KEY_PREFIX + latest);
        if (raw) {
          const parsed = JSON.parse(raw);
          setActiveBulkJob(parsed);
        }
      }
    } catch (_) { }
  }, []);

  const startClientBulkJob = async ({ candidates = [], candidateIds = null, startIndex = 0, pctCost = 0, pctMargin = 0, pctSale = 0, noModifyStock = false, ecommerceAction = '', alicuotaIVACode, jobId }) => {
    // candidates: optional snapshot of product objects
    // candidateIds: preferred persistent list of product IDs to process (exact resume)
    // startIndex: index within candidateIds to resume from
    const id = jobId || `job-${Date.now()}`;
    let ids = candidateIds;
    if (!ids) {
      // build ids from provided candidates snapshot
      ids = Array.isArray(candidates) ? candidates.map(p => p && p._id).filter(Boolean) : [];
    }
    if (!Array.isArray(ids) || ids.length === 0) return null;
    // Persist local overrides so Sales UI can allow sales immediately while the job runs
    try { setLocalNoControlForIds(ids, !!noModifyStock); } catch (_) { }
    const total = ids.length;
    const job = {
      id,
      createdAt: new Date().toISOString(),
      status: 'running', // running | paused | cancelled | done
      total,
      done: startIndex || 0,
      errors: [],
      currentIndex: startIndex || 0,
      candidateIds: ids,
      meta: { pctCost, pctMargin, pctSale, noModifyStock, ecommerceAction, alicuotaIVACode }
    };
    setActiveBulkJob(job);
    persistJob(job);
    // control object for pause/cancel
    bulkJobControlRef.current[id] = { pause: false, cancel: false };

    const CONCURRENCY = 10;
    let done = job.done;
    try {
      for (let i = job.currentIndex; i < total; i += CONCURRENCY) {
        const ctrl = bulkJobControlRef.current[id] || {};
        if (ctrl.cancel) {
          job.status = 'cancelled';
          persistJob(job);
          setActiveBulkJob({ ...job });
          return job;
        }
        while (ctrl.pause) {
          job.status = 'paused';
          persistJob(job);
          setActiveBulkJob({ ...job });
          // sleep until unpaused or cancelled
          // eslint-disable-next-line no-await-in-loop
          await new Promise((res) => setTimeout(res, 400));
          if ((bulkJobControlRef.current[id] || {}).cancel) break;
        }
        // still running
        job.status = 'running';
        persistJob(job);
        setActiveBulkJob({ ...job });
        const batchIds = job.candidateIds.slice(i, i + CONCURRENCY);
        const batch = batchIds.map((pid) => products.find(p => p && (p._id === pid || p.id === pid))).filter(Boolean);
        // execute patch requests in parallel for the batch
        // eslint-disable-next-line no-await-in-loop
        // eslint-disable-next-line no-loop-func
        await Promise.all(batch.map(async (p) => {
          if ((bulkJobControlRef.current[id] || {}).cancel) return;
          try {
            // Apply adjustments honoring dependency rules:
            // - If pctCost/pctMargin change, recalc salePrice = cost * (1 + margin/100)
            // - If pctSale provided (non-zero), apply sale change and recompute margin = (sale-cost)/cost*100
            const oldCost = Number(p.costPrice || 0);
            const oldMargin = Number(p.margin || 0);
            const oldSale = Number(p.salePrice || 0);

            const pctC = Number(pctCost || 0);
            const pctM = Number(pctMargin || 0);
            const pctS = Number(pctSale || 0);

            const newCost = round2(oldCost * (1 + pctC / 100));
            let newMargin = round2(oldMargin * (1 + pctM / 100));

            let newSale;
            if (pctS && Math.abs(pctS) > 1e-9) {
              // sale explicitly adjusted: apply and recompute margin
              newSale = round2(oldSale * (1 + pctS / 100));
              if (newCost > 0) {
                const computedMargin = ((newSale - newCost) / newCost) * 100;
                // avoid NaN/Infinity
                newMargin = round2(isFinite(computedMargin) ? computedMargin : newMargin);
              }
            } else {
              // no explicit sale change: compute sale from cost and margin
              newSale = round2(newCost * (1 + newMargin / 100));
            }

            const payload = { ...p };
            payload.costPrice = newCost;
            payload.margin = newMargin;
            payload.salePrice = newSale;
            payload.noControlStock = !!noModifyStock;
            if (ecommerceAction === 'enable') payload.sellOnline = true;
            if (ecommerceAction === 'disable') payload.sellOnline = false;
            if (alicuotaIVACode !== undefined && alicuotaIVACode !== null && alicuotaIVACode !== '') {
              const parsed = Number(alicuotaIVACode);
              if (!Number.isNaN(parsed)) payload.alicuotaIVACode = parsed;
            }
            await http.patch(ENDPOINTS.update, payload).catch(err => {
              // record error summary
              try { job.errors.push({ id: p._id, msg: err?.response?.data?.message || err.message || String(err) }); } catch (_) { }
            });
          } catch (err) {
            try { job.errors.push({ id: p._id, msg: err?.message || String(err) }); } catch (_) { }
          } finally {
            done++;
            job.done = done;
            job.currentIndex = i + batchIds.length; // advance by number of ids attempted
            persistJob(job);
            setActiveBulkJob({ ...job });
          }
        }));
      }
      job.status = 'done';
      persistJob(job);
      setActiveBulkJob({ ...job });
      // refresh products after completion
      try { await fetchProducts(); } catch (_) { }
      return job;
    } catch (err) {
      job.status = 'error';
      try { job.errors.push({ msg: err?.message || String(err) }); } catch (_) { }
      persistJob(job);
      setActiveBulkJob({ ...job });
      return job;
    }
  };

  const pauseJob = (id) => {
    const ctrl = bulkJobControlRef.current[id] || {};
    ctrl.pause = true;
    bulkJobControlRef.current[id] = ctrl;
    try {
      const raw = localStorage.getItem(JOB_KEY_PREFIX + id);
      if (raw) { const j = JSON.parse(raw); j.status = 'paused'; localStorage.setItem(JOB_KEY_PREFIX + id, JSON.stringify(j)); setActiveBulkJob(j); }
    } catch (_) { }
  };

  const resumeJob = (id) => {
    const ctrl = bulkJobControlRef.current[id] || {};
    ctrl.pause = false;
    bulkJobControlRef.current[id] = ctrl;
    try {
      const raw = localStorage.getItem(JOB_KEY_PREFIX + id);
      if (raw) {
        const j = JSON.parse(raw);
        j.status = 'running';
        localStorage.setItem(JOB_KEY_PREFIX + id, JSON.stringify(j));
        setActiveBulkJob(j);
        // kick runner using stored candidateIds and currentIndex
        startClientBulkJob({ candidateIds: j.candidateIds, startIndex: j.currentIndex || 0, pctCost: j.meta.pctCost, pctMargin: j.meta.pctMargin, pctSale: j.meta.pctSale, noModifyStock: j.meta.noModifyStock, ecommerceAction: j.meta.ecommerceAction, alicuotaIVACode: j.meta.alicuotaIVACode, jobId: id });
      }
    } catch (_) { }
  };

  const cancelJob = (id) => {
    const ctrl = bulkJobControlRef.current[id] || {};
    ctrl.cancel = true;
    bulkJobControlRef.current[id] = ctrl;
    try {
      const raw = localStorage.getItem(JOB_KEY_PREFIX + id);
      if (raw) {
        const j = JSON.parse(raw);
        j.status = 'cancelled';
        localStorage.setItem(JOB_KEY_PREFIX + id, JSON.stringify(j));
        setActiveBulkJob(j);
      }
      // remove job immediately from UI/storage so the panel does not stick around
      removeJob(id);
      setActiveBulkJob(null);
    } catch (_) { }
  };

  // Auto-dismiss finished/errored/cancelled jobs after a short delay so the panel doesn't stick
  useEffect(() => {
    try {
      const status = activeBulkJob?.status;
      const id = activeBulkJob?.id;
      if (!status || !id) return;
      if (status === 'done' || status === 'error' || status === 'cancelled') {
        const t = setTimeout(() => {
          try { removeJob(id); } catch (_) { }
          try { setActiveBulkJob(null); } catch (_) { }
        }, 4000); // 4s visible then auto-hide
        return () => clearTimeout(t);
      }
    } catch (_) { }
  }, [activeBulkJob?.status, activeBulkJob?.id]);


  const handleExport = async () => {
    try {
      const res = await http.get(ENDPOINTS.export, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err.response?.data || err.message);
      toast.error('❌ Error al exportar productos');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // Headers required by the import routine
      const headers = ['name', 'category', 'barcode', 'stock', 'costPrice', 'margin', 'salePrice (SIN IVA)', 'image', 'sellOnline', 'onSale', 'supplier', 'alicuotaIVACode'];
      // Optional sample row to guide users
      const sample = ['Ejemplo producto', 'Bebidas', '1234567890123', 10, 50.0, 20, 60.0, 'https://url-de-tu-foto.com/img.jpg', 'SI', 'NO', 'Distribuidora S.A.', 5];

      // Add explanation row for IVA codes
      const explanation = ['', '', '', '', '', '', 'Precio SIN IVA', 'URL de imagen opcional', 'SI/NO', 'SI/NO', '', 'Códigos: 0=Exento, 3=10.5%, 4=27%, 5=21%, 6=2.5%'];

      const ws = XLSX.utils.aoa_to_sheet([headers, sample, explanation]);

      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 20 }, // name
        { wch: 15 }, // category
        { wch: 15 }, // barcode
        { wch: 8 },  // stock
        { wch: 12 }, // costPrice
        { wch: 10 }, // margin
        { wch: 18 }, // salePrice (SIN IVA)
        { wch: 30 }, // image
        { wch: 10 }, // sellOnline
        { wch: 10 }, // onSale
        { wch: 20 }, // supplier
        { wch: 50 }  // alicuotaIVACode (wider for explanation)
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
      // Use the filename requested by the user: "plantilla de ejemplo.xlsx"
      XLSX.writeFile(wb, 'plantilla de ejemplo.xlsx');
      toast.info('📄 Plantilla descargada');
    } catch (err) {
      console.error('Error creando plantilla xlsx:', err);
      toast.error('❌ Error al generar la plantilla');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Pre-check headers client-side (non-blocking)
    try {
      // Do the heavy XLSX read in an idle callback / timeout so the selection UI doesn't jank
      const runParse = async () => {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const wb = XLSX.read(arrayBuffer, { type: 'array' });
          const firstSheet = wb.SheetNames[0];
          const rows = XLSX.utils.sheet_to_json(wb.Sheets[firstSheet], { header: 1 });
          const headerRow = Array.isArray(rows) && rows.length > 0 ? rows[0].map(String) : [];
          const hasAlic = headerRow.some((h) => /alicuota/i.test(h));
          if (hasAlic) toast.warn('La columna "alicuotaIVACode" será ignorada durante la importación. El IVA se usa desde la configuración fiscal.');
        } catch (err) {
          // ignore parse errors in pre-check
        }
      };
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => { runParse().catch(() => { }); }, { timeout: 500 });
      } else {
        setTimeout(() => { runParse().catch(() => { }); }, 40);
      }
    } catch (err) {
      console.debug('No se pudo leer el archivo localmente para pre-check de encabezados', err);
    }

    const form = new FormData();
    form.append('file', file);
    let wasCanceled = false;

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadLoaded(0);
      setUploadTotal(0);
      setUploadPhase('uploading');

      // create abort controller so user can cancel
      abortCtrlRef.current = new AbortController();

      const res = await http.post(ENDPOINTS.import, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortCtrlRef.current.signal,
        onUploadProgress: (progressEvent) => {
          try {
            const loaded = progressEvent.loaded || 0;
            const total = progressEvent.total || 0;
            // Throttle state updates to at most ~120ms to avoid UI churn on fast progress events
            const now = Date.now();
            const minInterval = 120; // ms
            // Always update loaded/total immediately (cheap) but throttle pct updates
            setUploadLoaded(loaded);
            setUploadTotal(total);
            if (total) {
              const pct = Math.round((loaded * 100) / total);
              const lastPct = lastProgressPctRef.current;
              if (pct !== lastPct && (now - lastProgressAtRef.current) > minInterval) {
                lastProgressAtRef.current = now;
                lastProgressPctRef.current = pct;
                setUploadProgress(pct);
              }
              if (loaded >= total) setUploadPhase('processing');
            } else {
              const approx = Math.min(99, Math.round((loaded / 1024) % 100));
              const lastPct = lastProgressPctRef.current;
              if (approx !== lastPct && (now - lastProgressAtRef.current) > minInterval) {
                lastProgressAtRef.current = now;
                lastProgressPctRef.current = approx;
                setUploadProgress(approx);
              }
            }
          } catch (_) { }
        }
      });

      const created = res.data.created || 0;
      const updated = res.data.updated || 0;
      const unchanged = res.data.unchanged || 0;
      const changesCount = Array.isArray(res.data.changes) ? res.data.changes.length : 0;
      toast.success(`✅ Importado: ${created} creados, ${updated} actualizados, ${unchanged} sin cambios`);
      if (changesCount > 0) toast.info(`🔁 Detalles actualizados: ${changesCount}`);
      if (res.data.errors && res.data.errors.length) {
        console.warn('Import errors:', res.data.errors);
        toast.warn(`Algunas filas no fueron importadas: ${res.data.errors.length}`);
        setImportErrors(res.data.errors);
      } else setImportErrors([]);
      fetchProducts();
      focusSearch();
    } catch (err) {
      const isCanceled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.message === 'canceled' || err?.message === 'Request aborted';
      if (isCanceled) {
        wasCanceled = true;
        setUploading(false);
        setUploadProgress(0);
        setUploadLoaded(0);
        setUploadTotal(0);
        setUploadPhase('idle');
        toast.info('⏹️ Importación cancelada');
        focusSearch();
      } else {
        console.error('Error importing:', err.response?.data || err.message);
        const msg = err.response?.data?.message || '❌ Error al importar';
        toast.error(msg);
        setUploadPhase('done');
        focusSearch();
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!wasCanceled) setUploadPhase('done');
      if (abortCtrlRef.current) {
        try { abortCtrlRef.current = null; } catch (_) { }
      }
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadLoaded(0);
        setUploadTotal(0);
        setUploadPhase('idle');
      }, 900);
    }
  };

  useEffect(() => {
    let result = (Array.isArray(products) ? products : []).filter(
      (p) =>
        p.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        p.category.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        p.barcode?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );

    // Client-side sorting
    const comparator = (a, b) => {
      const col = sortBy;
      const va = a[col];
      const vb = b[col];
      // handle undefined/null
      if (va == null && vb == null) return 0;
      if (va == null) return -1;
      if (vb == null) return 1;

      // numeric columns: stock, costPrice, margin, salePrice
      const numericCols = ['stock', 'costPrice', 'margin', 'salePrice'];
      if (numericCols.includes(col)) {
        const na = Number(va) || 0;
        const nb = Number(vb) || 0;
        return na - nb;
      }

      // default string compare
      return String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
    };

    result = result.slice().sort((a, b) => {
      const cmp = comparator(a, b);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    if (filterOnline) {
      result = result.filter(p => !!p.sellOnline);
    }

    setFiltered(result);
  }, [debouncedSearch, products, sortBy, sortDirection, filterOnline]);

  // Handle page reset only when search/filters change (avoid reset on product updates/photo upload)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterOnline]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pagedProducts = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Cargar proveedores cuando se abre el modal
  useEffect(() => {
    if (showModal) {
      fetchSuppliers();
    }
  }, [showModal, fetchSuppliers]);

  const handleSelectProduct = useCallback((id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addToPrintQueue = useCallback((id) => {
    setPrintQueueIds(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      localStorage.setItem('printQueueIds', JSON.stringify(next));
      toast.info('🏷️ Agregado a cola de impresión', { autoClose: 2000 });
      return next;
    });
  }, []);

  const clearPrintQueue = useCallback(() => {
    if (window.confirm('¿Vaciar la cola de etiquetas pendientes?')) {
      setPrintQueueIds([]);
      localStorage.removeItem('printQueueIds');
      setShowLabelModal(false);
      setViewingQueue(false);
    }
  }, []);

  const handleOpenQueue = useCallback(() => {
    setViewingQueue(true);
    setShowLabelModal(true);
  }, []);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedProductIds(new Set(filtered.map(p => p._id)));
    } else {
      setSelectedProductIds(new Set());
    }
  }, [filtered]);

  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  }, [sortBy]);

  // 🔹 Actualiza salePrice si cambian costPrice o margin
  const handleAutoCalc = (field, value) => {
    // PRESERVAR INPUT: No convertir a Number inmediatamente para el campo editado,
    // mantener el string (value) para permitir escritura fluida (ej: "10.").
    const valNum = parseFloat(value);
    const numSafe = isNaN(valNum) ? 0 : valNum;

    // Almacenar value tal cual para el campo que se está editando
    const updated = { ...formData, [field]: value };

    // Helpers para valores numéricos de otros campos
    const cost = parseFloat(updated.costPrice) || 0;
    const margin = parseFloat(updated.margin) || 0;

    if (field === 'costPrice') {
      // Si cambia costo, recalcular venta (si hay margen definido)
      if (updated.margin !== '' && updated.margin !== undefined) {
        updated.salePrice = round2(numSafe * (1 + margin / 100));
      }
    } else if (field === 'salePrice') {
      // Si cambia venta, recalcular margen (si hay costo > 0)
      if (cost !== 0) {
        updated.margin = round2(((numSafe - cost) / cost) * 100);
      }
    } else if (field === 'margin') {
      // Si cambia margen, recalcular venta
      updated.salePrice = round2(cost * (1 + numSafe / 100));
    }

    // No redondeamos globalmente todo el objeto 'updated' para no perder el input del usuario en 'field'
    setFormData(updated);
  };

  // Manejar cambio en el campo de precio con IVA
  const handleSalePriceWithIVA = (value) => {
    const valNum = parseFloat(value);
    const numSafe = isNaN(valNum) ? 0 : valNum;

    // Obtener el porcentaje de IVA del producto
    const ivaCode = formData.alicuotaIVACode ?? 5;
    const ivaPct = ivaCode === 0 ? 0 : getAlicuotaPercent(ivaCode) || 0;
    const ivaMultiplier = 1 + (ivaPct / 100);

    // Convertir el precio con IVA a precio sin IVA
    // Controlamos decimales a 2 como pidió el usuario.
    const salePriceWithoutIVA = ivaCode === 0 ? numSafe : Number((numSafe / ivaMultiplier).toFixed(6));

    const updated = { ...formData, salePrice: salePriceWithoutIVA };

    // Calcular el margen basándose en el precio sin IVA exacto
    const cost = parseFloat(updated.costPrice) || 0;
    if (cost !== 0) {
      updated.margin = round2(((salePriceWithoutIVA - cost) / cost) * 100);
    }

    setFormData(updated);
  };


  // 🔹 Guardar producto
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('❌ No estás logueado');
      return;
    }

    if (!formData.name || !formData.category || !formData.barcode) {
      toast.warning('⚠️ Por favor completa nombre, categoría y código de barras');
      return;
    }

    const uppercaseName = (formData.name || '').toUpperCase();
    const uppercaseCategory = (formData.category || '').toUpperCase();

    // Validación: precio de venta debe ser mayor al de compra
    if (parseFloat(formData.salePrice) <= parseFloat(formData.costPrice)) {
      toast.error('❌ El precio de venta debe ser mayor al precio de compra');
      return;
    }

    // Validación: stock no puede ser negativo
    if (parseFloat(formData.stock) < 0) {
      toast.error('❌ El stock no puede ser negativo');
      return;
    }

    try {
      // prepare payload and ensure alicuotaIVACode is 0 when emitter does not discriminar IVA
      const payload = {
        ...formData,
        name: uppercaseName,
        category: uppercaseCategory
      };
      if (!discriminarIVA) payload.alicuotaIVACode = 0;

      // Normalize numeric discount fields before sending
      const normalized = {
        ...payload,
        discountValue: payload.discountValue !== undefined ? Number(payload.discountValue) : 0,
        discountTiers: Array.isArray(payload.discountTiers)
          ? payload.discountTiers.map((t) => ({
            minQty: Number(t.minQty) || 0,
            maxQty: t.maxQty === '' || t.maxQty === undefined ? undefined : Number(t.maxQty),
            type: t.type,
            value: Number(t.value) || 0,
          }))
          : [],
      };
      // Normalize combo fields if present
      if (payload.isCombo) {
        normalized.isCombo = true;
        normalized.components = Array.isArray(payload.components) ? payload.components.map((c) => ({
          productId: c.productId,
          qty: Number(c.qty) || 1,
          priceOverride: (c.priceOverride === '' || c.priceOverride === undefined) ? undefined : Number(String(c.priceOverride).replace(/,/g, '.'))
        })) : [];
        normalized.comboPrice = (payload.comboPrice === '' || payload.comboPrice === undefined) ? undefined : Number(String(payload.comboPrice).replace(/,/g, '.'));
      } else {
        normalized.isCombo = false;
        normalized.components = [];
        normalized.comboPrice = undefined;
      }
      // Enforce 2 decimals for numeric price/margin fields
      normalized.costPrice = round2(Number(normalized.costPrice) || 0);
      normalized.margin = round2(Number(normalized.margin) || 0);
      normalized.salePrice = round2(Number(normalized.salePrice) || 0);

      // Validate supplierId empty
      if (!normalized.supplierId) {
        delete normalized.supplierId;
      }

      if (editMode) {
        const res = await http.patch(ENDPOINTS.update, normalized);
        console.log('Producto actualizado:', res.data);
        // On successful server save, remove any local overrides for this product (server is source of truth)
        try { removeLocalNoControlForIds([normalized._id]); } catch (_) { }
        addToPrintQueue(normalized._id);
        toast.success('✅ Producto actualizado correctamente');
      } else {
        const { _id, ...dataToSend } = normalized; // no enviar _id al crear
        // ensure numeric types
        const res = await http.post(ENDPOINTS.add, dataToSend);
        console.log('Producto creado:', res.data);
        if (res.data && res.data._id) addToPrintQueue(res.data._id);
        toast.success('✅ Producto creado correctamente');
      }

      fetchProducts();
      try { dispatch(getCategories()); } catch (_) { }
      setShowModal(false);
      setEditMode(false);
      setFormData({
        _id: '',
        name: '',
        category: '',
        stock: 0,
        costPrice: 0,
        margin: 0,
        salePrice: 0,
        barcode: '',
        alicuotaIVACode: discriminarIVA ? 5 : 0,
        noControlStock: false,
      });
      setError('');
      focusSearch();
    } catch (err) {
      console.error(err.response?.data || err.message);
      const errorMsg = err.response?.data?.message || '❌ Error al guardar producto';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleQuickStockSearch = (barcode) => {
    const p = products.find(prod => prod.barcode === barcode);
    if (p) {
      setQuickStockData(prev => ({ ...prev, product: p, costPrice: p.costPrice }));
    } else {
      setQuickStockData(prev => ({ ...prev, product: null }));
    }
  };

  const handleQuickStockSubmit = async (e) => {
    e.preventDefault();
    const { product, stockToAdd, costPrice, salePrice, margin } = quickStockData;
    if (!product) return;

    try {
      const payload = {
        _id: product._id,
        stock: round2(Number(product.stock) + (Number(stockToAdd) || 0)),
      };

      // Only apply explicit changes from the modal
      if (costPrice && Number(costPrice) !== product.costPrice) {
        const newCost = Number(costPrice);
        payload.costPrice = round2(newCost);

        // Mantener el margen actual y actualizar el precio de venta consecuentemente
        // (y por tanto, el precio con IVA que se muestra en el listado)
        const currentMargin = product.margin || 0;
        payload.salePrice = round2(newCost * (1 + currentMargin / 100));
      }
      if (margin && Number(margin) !== product.margin) {
        payload.margin = round2(Number(margin));
      }
      if (salePrice && Number(salePrice) !== product.salePrice) {
        payload.salePrice = round2(Number(salePrice));
      }

      /* 
      REMOVED: Automatic recalculations. These were causing prices to "change by themselves"
      when the user updated costs but didn't intend to update sale prices yet.
      Now we only update what the user explicitly touched in the modal.
      */

      await http.patch(ENDPOINTS.update, payload);
      toast.success('✅ Producto actualizado');
      fetchProducts();
      setShowQuickStockModal(false);
      setQuickStockData({ barcode: '', stockToAdd: '', costPrice: '', salePrice: '', margin: '', product: null });
      focusSearch();
    } catch (err) {
      toast.error('❌ Error al actualizar');
      console.error(err);
    }
  };

  // --- Lógica de la Estación de Fotos Rápida ---
  useEffect(() => {
    if (showPhotoMode && photoModeInputRef.current) {
      photoModeInputRef.current.focus();
    }
  }, [showPhotoMode]);

  const handlePhotoModeSearch = useCallback((code) => {
    if (!code) return;
    const p = products.find(prod => String(prod.barcode) === String(code));
    if (p) {
      setPhotoModeProduct(p);
    } else {
      setPhotoModeProduct(null);
    }
  }, [products]);

  const onScanDetected = useCallback((code) => {
    setPhotoModeBarcode(code);
    handlePhotoModeSearch(code);
    setIsScanningPhotoCode(false);
    if (navigator.vibrate) navigator.vibrate(50);
  }, [handlePhotoModeSearch]);

  const handlePhotoModeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !photoModeProduct) return;

    setPhotoModeLoading(true);
    const loadingToast = toast.loading(`Subiendo foto para ${photoModeProduct.name}...`);

    try {
      const fd = new FormData();
      fd.append('image', file);
      const uploadRes = await http.post('/product/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newImageUrl = uploadRes.data.imageUrl;
      const updatedImages = [newImageUrl, ...(photoModeProduct.ecommerceImages || [])];

      const updatedProduct = {
        ...photoModeProduct,
        ecommerceImages: updatedImages
      };

      await http.patch(ENDPOINTS.update, updatedProduct);

      toast.update(loadingToast, {
        render: `✅ ${photoModeProduct.name} actualizado`,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });

      // Actualizar lista global para que el cambio se vea en la tabla principal
      setProducts(prev => prev.map(p => p._id === photoModeProduct._id ? updatedProduct : p));

      // Mantener el producto seleccionado para que vea la foto que acaba de subir
      // setPhotoModeBarcode('');
      // setPhotoModeProduct(null);
      // if (photoModeInputRef.current) photoModeInputRef.current.focus();

      // Actualizar el producto actual en el modal con la nueva lista de imágenes
      setPhotoModeProduct(updatedProduct);
    } catch (err) {
      console.error(err);
      toast.update(loadingToast, {
        render: '❌ Error al procesar foto',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setPhotoModeLoading(false);
      e.target.value = '';
    }
  };

  const handleDeletePhotoFromMode = async (idx) => {
    if (!photoModeProduct) return;
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta foto?')) return;

    try {
      const updatedImages = [...(photoModeProduct.ecommerceImages || [])];
      updatedImages.splice(idx, 1);

      const updatedProduct = {
        ...photoModeProduct,
        ecommerceImages: updatedImages
      };

      await http.patch(ENDPOINTS.update, updatedProduct);

      // Actualizar estado local para el modal
      setPhotoModeProduct(updatedProduct);

      // Actualizar lista global para la tabla de fondo
      setProducts(prev => prev.map(p => p._id === photoModeProduct._id ? updatedProduct : p));

      toast.success('Foto eliminada correctamente');
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar foto');
    }
  };


  // Combo helpers: add/remove/edit components and compute comboPrice
  const handleAddComponent = () => {
    setFormData((prev) => ({
      ...prev,
      isCombo: true,
      components: [...(Array.isArray(prev.components) ? prev.components : []), { productId: '', qty: 1, priceOverride: '' }]
    }));
  };

  const handleRemoveComponent = (idx) => {
    setFormData((prev) => {
      const comps = Array.isArray(prev.components) ? [...prev.components] : [];
      comps.splice(idx, 1);
      return { ...prev, components: comps };
    });
  };

  const calculateComboCostFromComponents = () => {
    if (!formData.components || formData.components.length === 0) {
      toast.warn('⚠️ No hay componentes para calcular el costo.');
      return;
    }

    let totalCost = 0;
    let missingCosts = 0;

    formData.components.forEach(comp => {
      const prod = products.find(p => String(p._id) === String(comp.productId));
      if (prod) {
        const cost = Number(prod.costPrice) || 0;
        if (cost === 0) missingCosts++;
        totalCost += cost * (Number(comp.qty) || 1);
      } else {
        missingCosts++;
      }
    });

    if (totalCost === 0 && missingCosts > 0) {
      toast.error('❌ No se pudo calcular el costo (los productos componentes tienen costo 0 o no se encontraron).');
      return;
    }

    const roundedCost = round2(totalCost);
    setFormData(prev => {
      const updated = { ...prev, costPrice: roundedCost };
      // Also update margin if salePrice exists
      const sale = Number(updated.salePrice) || 0;
      if (sale > 0 && roundedCost > 0) {
        updated.margin = round2(((sale - roundedCost) / roundedCost) * 100);
      }
      return updated;
    });

    if (missingCosts > 0) {
      toast.warn(`⚠️ Costo calculado: $${roundedCost}. Nota: ${missingCosts} componente(s) no tienen costo definido.`);
    } else {
      toast.success(`✅ Costo calculado correctamente: $${roundedCost}`);
    }
  };

  const handleComponentChange = (idx, field, value) => {
    setFormData((prev) => {
      const comps = Array.isArray(prev.components) ? [...prev.components] : [];
      const existing = comps[idx] || { productId: '', qty: 1, priceOverride: '' };
      const next = { ...existing };
      if (field === 'qty') next.qty = Number(value) || 1;
      else if (field === 'priceOverride') {
        const v = value === '' ? '' : Number(String(value).replace(/,/g, '.'));
        next.priceOverride = v === '' ? '' : v;
      } else next.productId = value;
      comps[idx] = next;
      return { ...prev, components: comps, isCombo: true };
    });
  };

  // Validación inline para el modal: precio de venta debe ser mayor a costo y campos requeridos
  const isModalValid = () => {
    if (!formData.name || !formData.category || !formData.barcode) return false;
    if (Number(formData.salePrice) <= Number(formData.costPrice)) return false;
    if (Number(formData.stock) < 0) return false;
    // Discount validations
    if (formData.discountType === 'percent') {
      const v = Number(formData.discountValue) || 0;
      if (v < 0 || v > 100) return false;
    }
    if (formData.discountType === 'fixed') {
      const v = Number(formData.discountValue) || 0;
      if (v < 0) return false;
      if (v >= Number(formData.salePrice)) return false; // cannot be >= price
    }
    if (formData.discountType === 'tiered') {
      // basic tier validation
      for (const t of formData.discountTiers || []) {
        const min = Number(t.minQty) || 0;
        const max = t.maxQty !== undefined && t.maxQty !== '' ? Number(t.maxQty) : undefined;
        if (min < 1) return false;
        if (max !== undefined && max < min) return false;
        if (!['percent', 'fixed'].includes(t.type)) return false;
        if (t.type === 'percent' && (t.value < 0 || t.value > 100)) return false;
        if (t.type === 'fixed' && t.value < 0) return false;
      }
    }
    // Combo validations
    if (formData.isCombo) {
      if (!Array.isArray(formData.components) || formData.components.length === 0) return false;
      for (const c of formData.components) {
        if (!c.productId) return false;
        if ((Number(c.qty) || 0) < 1) return false;
      }
    }
    return true;
  };

  // Devuelve mensajes explicativos para mostrar en el modal cuando hay errores de validación
  const getModalValidationErrors = () => {
    const errors = [];
    if (!formData.name) errors.push('El nombre del producto es obligatorio.');
    if (!formData.category) errors.push('La categoría es obligatoria.');
    if (!formData.barcode) errors.push('El código (barcode) es obligatorio.');
    if (Number(formData.salePrice) <= Number(formData.costPrice)) errors.push('El precio de venta debe ser mayor que el precio de costo.');
    if (Number(formData.stock) < 0) errors.push('El stock no puede ser negativo.');

    // Descuentos básicos
    if (formData.discountType === 'percent') {
      const v = Number(formData.discountValue) || 0;
      if (v < 0 || v > 100) errors.push('El porcentaje de descuento debe estar entre 0 y 100.');
    }
    if (formData.discountType === 'fixed') {
      const v = Number(formData.discountValue) || 0;
      if (v < 0) errors.push('El descuento fijo no puede ser negativo.');
      if (v >= Number(formData.salePrice)) errors.push('El descuento fijo no puede ser mayor o igual al precio de venta.');
    }

    // Validaciones de combo con mensajes por componente
    if (formData.isCombo) {
      if (!Array.isArray(formData.components) || formData.components.length === 0) {
        errors.push('El combo debe tener al menos un componente.');
      } else {
        formData.components.forEach((c, idx) => {
          const num = idx + 1;
          if (!c.productId) errors.push(`Componente #${num}: falta seleccionar un producto.`);
          if ((Number(c.qty) || 0) < 1) errors.push(`Componente #${num}: la cantidad debe ser al menos 1.`);
          // Si el producto seleccionado como componente es a su vez un combo,
          // mostrar mensaje claro (el backend ya impide combos anidados pero mostramos antes de enviar).
          try {
            const prod = products.find((p) => String(p._id) === String(c.productId));
            if (prod && prod.isCombo) {
              errors.push(`Componente #${num}: el producto seleccionado está marcado como combo (no permitido).`);
            }
          } catch (e) { }
        });
      }
    }

    return errors;
  };

  const handleEdit = useCallback((p) => {
    setFormData({
      _id: p._id,
      name: p.name || '',
      category: (() => {
        const productCat = (p.category || '').trim();
        if (!productCat) return '';
        // Find matching category in list ignoring case
        const match = categories.find(c => (c.category || '').trim().toLowerCase() === productCat.toLowerCase());
        return match ? match.category : productCat;
      })(),
      stock: p.stock,
      costPrice: round2(p.costPrice),
      margin: round2(p.margin),
      salePrice: round2(p.salePrice),
      barcode: (p.barcode || '').toUpperCase(),
      alicuotaIVACode: p.alicuotaIVACode ?? (discriminarIVA ? 5 : 0),
      noControlStock: !!p.noControlStock,
      variants: Array.isArray(p.variants) ? p.variants : [],
      // Load discount fields if present on product, otherwise defaults
      discountType: p.discountType || 'none',
      discountValue: typeof p.discountValue === 'number' ? p.discountValue : 0,
      discountTiers: Array.isArray(p.discountTiers) ? p.discountTiers : [],
      // Combo fields
      isCombo: !!p.isCombo,
      components: Array.isArray(p.components) ? p.components : [],
      comboPrice: p.comboPrice || '',
      // E-commerce fields
      sellOnline: !!p.sellOnline,
      ecommerceDescription: p.ecommerceDescription || '',
      ecommerceImages: Array.isArray(p.ecommerceImages) ? p.ecommerceImages : [],
      onSale: !!p.onSale,
      // Supplier
      supplierId: p.supplierId || '',
    });
    setIsManualCategory(false);
    setEditMode(true);
    setShowModal(true);
  }, [discriminarIVA, categories]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('⚠️ ¿Estás seguro de eliminar este producto?\n\nEsta acción no se puede deshacer.')) {
      return;
    }

    try {
      await http.delete(ENDPOINTS.delete(id));
      fetchProducts();
      toast.success('✅ Producto eliminado correctamente');
      focusSearch();
    } catch (err) {
      console.error(err.response?.data || err.message);
      const errorMsg = '❌ Error al eliminar producto';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }, [fetchProducts, focusSearch]);

  // Eliminar productos de una categoría específica (DEV)
  const handleDeleteByCategory = useCallback(async () => {
    if (!deleteCategoryName.trim()) {
      toast.error('❌ Debes ingresar un nombre de categoría');
      return;
    }

    // Filtrar productos de la categoría especificada
    const categoryProducts = products.filter(p =>
      (p.category || '').toUpperCase() === deleteCategoryName.trim().toUpperCase()
    );

    if (categoryProducts.length === 0) {
      toast.info(`No hay productos en la categoría "${deleteCategoryName}"`);
      return;
    }

    // Verificar confirmación
    if (deleteConfirmText !== 'ELIMINAR') {
      toast.error('❌ Debes escribir "ELIMINAR" para confirmar');
      return;
    }

    try {
      let deleted = 0;
      let errors = 0;

      toast.info(`Eliminando ${categoryProducts.length} productos de "${deleteCategoryName}"...`);

      for (const product of categoryProducts) {
        try {
          await http.delete(ENDPOINTS.delete(product._id));
          deleted++;
        } catch (err) {
          console.error(`✗ Error eliminando ${product.name}:`, err);
          errors++;
        }
      }

      fetchProducts();
      setShowDeleteByCategoryModal(false);
      setDeleteCategoryName('');
      setDeleteConfirmText('');
      focusSearch();

      if (errors === 0) {
        toast.success(`✅ ${deleted} productos eliminados de "${deleteCategoryName}"`);
      } else {
        toast.warning(`⚠️ ${deleted} productos eliminados, ${errors} errores`);
      }
    } catch (err) {
      console.error(err);
      toast.error('❌ Error al eliminar productos');
    }
  }, [products, fetchProducts, deleteCategoryName, deleteConfirmText, focusSearch]);


  // Toggle per-product 'no control stock' (permite vender aunque stock <= 0)
  const handleToggleNoControl = useCallback(async (p, e) => {
    try {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      const id = p._id;
      const newVal = !p.noControlStock;
      // optimistic update in UI
      setProducts((prev) => prev.map((it) => (it && it._id === id ? { ...it, noControlStock: newVal } : it)));
      setFiltered((prev) => prev.map((it) => (it && it._id === id ? { ...it, noControlStock: newVal } : it)));
      // set local override so Sales UI can allow selling immediately
      try { setLocalNoControlForIds([id], !!newVal); } catch (_) { }

      // persist to server
      const payload = { _id: id, noControlStock: !!newVal };
      const res = await http.patch(ENDPOINTS.update, payload);
      // on success, remove local override (server is source of truth)
      try { removeLocalNoControlForIds([id]); } catch (_) { }
      // update products with server response if provided
      if (res && res.data) {
        const updated = res.data;
        setProducts((prev) => prev.map((it) => (it && it._id === id ? { ...it, ...updated } : it)));
        setFiltered((prev) => prev.map((it) => (it && it._id === id ? { ...it, ...updated } : it)));
      }
      toast.success(`✅ Configuración actualizada: ${newVal ? 'Permitir venta sin stock' : 'Control de stock activo'}`);
    } catch (err) {
      console.error('toggle noControlStock error', err?.response?.data || err?.message || err);
      // keep local override so sales can still be tested; notify user
      toast.warn('⚠️ No se pudo persistir cambio en servidor; la anulación local queda activa para pruebas');
    }
  }, []);

  // Toggle per-product 'sellOnline' (e-commerce visibility)
  const handleToggleSellOnline = useCallback(async (p, e) => {
    try {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      const id = p._id;
      const newVal = !p.sellOnline;
      // optimistic update in UI
      setProducts((prev) => prev.map((it) => (it && it._id === id ? { ...it, sellOnline: newVal } : it)));
      setFiltered((prev) => prev.map((it) => (it && it._id === id ? { ...it, sellOnline: newVal } : it)));

      // persist to server
      const payload = { _id: id, sellOnline: !!newVal };
      const res = await http.patch(ENDPOINTS.update, payload);
      // update products with server response if provided
      if (res && res.data) {
        const updated = res.data;
        setProducts((prev) => prev.map((it) => (it && it._id === id ? { ...it, ...updated } : it)));
        setFiltered((prev) => prev.map((it) => (it && it._id === id ? { ...it, ...updated } : it)));
      }
      toast.success(`✅ Producto ${newVal ? 'publicado en tienda' : 'retirado de tienda'}`);
    } catch (err) {
      console.error('toggle sellOnline error', err?.response?.data || err?.message || err);
      toast.error('❌ Error al actualizar visibilidad en tienda');
      // revert optimistic update
      fetchProducts();
    }
  }, [fetchProducts]);


  // Compute discounted unit price for given formData and qty (client-side preview)
  const computeDiscountedPrice = useCallback((fd, qty = 1) => {
    const base = Number(fd.salePrice) || 0;
    if (!fd || !fd.discountType || fd.discountType === 'none') return round2(base);

    if (fd.discountType === 'percent') {
      const pct = Number(fd.discountValue) || 0;
      const net = base * (1 - pct / 100);
      return round2(Math.max(0, net));
    }

    if (fd.discountType === 'fixed') {
      const v = Number(fd.discountValue) || 0;
      const net = base - v;
      return round2(Math.max(0, net));
    }

    if (fd.discountType === 'tiered') {
      const tiers = Array.isArray(fd.discountTiers) ? fd.discountTiers : [];
      // find matching tier for qty (largest minQty <= qty and where qty <= maxQty if defined)
      let match = null;
      for (const t of tiers) {
        const min = Number(t.minQty) || 0;
        const max = t.maxQty !== undefined && t.maxQty !== '' ? Number(t.maxQty) : undefined;
        if (qty >= min && (max === undefined || qty <= max)) {
          if (!match || min > (Number(match.minQty) || 0)) match = t;
        }
      }
      if (!match) return round2(base);
      if (match.type === 'percent') {
        return round2(base * (1 - Number(match.value) / 100));
      }
      if (match.type === 'fixed') {
        return round2(Math.max(0, base - Number(match.value)));
      }
    }

    return round2(base);
  }, []);

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const formatBytes = (bytes) => {
    if (bytes == null || bytes === 0) return '0 B';
    const thresh = 1024;
    if (Math.abs(bytes) < thresh) return bytes + ' B';
    const units = ['KB', 'MB', 'GB', 'TB'];
    let u = -1;
    let b = Number(bytes);
    do {
      b /= thresh;
      ++u;
    } while (Math.abs(b) >= thresh && u < units.length - 1);
    return `${round2(b)} ${units[u]}`;
  };

  const formatDuration = (secs) => {
    if (secs == null || !isFinite(secs)) return null;
    const s = Math.round(secs);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}m ${rem}s`;
  };

  // Inject processing bar CSS once (indeterminate animation for server processing)
  useEffect(() => {
    const id = 'products-processing-styles';
    if (typeof document === 'undefined' || document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.innerHTML = `
      @keyframes productsStripeMove {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      .processing-stripes {
        height: 100%;
        width: 100%;
        background-image: linear-gradient(90deg, rgba(40,167,69,0.95) 25%, rgba(233,236,239,0.95) 25%, rgba(233,236,239,0.95) 50%, rgba(40,167,69,0.95) 50%, rgba(40,167,69,0.95) 75%, rgba(233,236,239,0.95) 75%, rgba(233,236,239,0.95) 100%);
        background-size: 200% 100%;
        animation: productsStripeMove 1.2s linear infinite;
        border-radius: 6px;
      }
    `;
    document.head.appendChild(s);
    return () => { /* keep styles for app life */ };
  }, []);

  // Simulate / advance progress while server is processing the uploaded file.
  // This keeps the percentage increasing until the server responds (uploadPhase becomes 'done').
  useEffect(() => {
    // clear any previous timer
    const clearTimer = () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };

    if (uploadPhase === 'processing') {
      // Start a gentle interval that increases the progress toward 99%
      clearTimer();
      processingStartRef.current = Date.now();
      progressSamplesRef.current = [];
      setProcessingEta(null);
      const PROCESSING_INTERVAL_MS = 900; // slightly slower updates to reduce UI churn
      processingTimerRef.current = setInterval(() => {
        setUploadProgress((prev) => {
          const now = Date.now();
          let next = prev;
          if (prev >= MAX_PROCESSING_PCT) {
            next = MAX_PROCESSING_PCT;
          } else {
            // increment by a small random delta that reduces over time
            const elapsedSec = Math.max(1, (now - processingStartRef.current) / 1000);
            // reduce delta as time passes so it slows down naturally; keep small steps
            const maxDelta = Math.max(1, Math.round(Math.max(1, 2 - Math.log10(elapsedSec))));
            const delta = Math.max(1, Math.round(Math.random() * maxDelta));
            next = Math.min(MAX_PROCESSING_PCT, prev + delta);
          }

          // push sample
          try {
            const samples = progressSamplesRef.current || [];
            samples.push({ t: now, p: next });
            // keep last 6 samples
            if (samples.length > 6) samples.splice(0, samples.length - 6);
            progressSamplesRef.current = samples;

            // compute velocity from samples (percent per second)
            if (samples.length >= 2) {
              const first = samples[0];
              const last = samples[samples.length - 1];
              const deltaP = last.p - first.p;
              const deltaT = (last.t - first.t) / 1000; // seconds
              if (deltaT > 0 && deltaP > 0) {
                const vel = deltaP / deltaT; // percent per sec
                const remain = Math.max(0, 100 - last.p);
                const etaSec = remain / vel;
                // clamp unrealistic ETAs and ignore very large estimates
                if (!isFinite(etaSec) || etaSec > 300 || vel < 0.02) {
                  setProcessingEta(null);
                } else {
                  const clamped = Math.min(60 * 60, Math.max(0, etaSec));
                  setProcessingEta(clamped);
                }
              }
            }
          } catch (_) { }

          return next;
        });
      }, PROCESSING_INTERVAL_MS);
    } else if (uploadPhase === 'done') {
      // Complete to 100% and clear the timer
      setUploadProgress(100);
      clearTimer();
      setProcessingEta(0);
    } else {
      // idle or uploading or other states -> ensure no timer running
      clearTimer();
    }

    return () => clearTimer();
  }, [uploadPhase]);

  const clearImportErrors = () => {
    setImportErrors([]);
  };

  // Cancel handler reusable so overlay and progress area can both call it
  const handleCancelImport = () => {
    try {
      if (abortCtrlRef.current) {
        abortCtrlRef.current.abort();
        abortCtrlRef.current = null;
      }
    } catch (_) { }
    if (fileInputRef.current) {
      try { fileInputRef.current.value = ''; } catch (_) { }
    }
    setUploading(false);
    setUploadPhase('idle');
    setUploadProgress(0);
    setUploadLoaded(0);
    setUploadTotal(0);
    toast.info('⏹️ Importación cancelada');
    focusSearch();
  };

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif', width: '100%', position: 'relative' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }} className="stack-sm">
        <h1>📦 Gestión de Productos</h1>
        <div style={{ display: 'flex', gap: '10px' }} className="stack-sm">
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          {!isEcommerceLite && (
            <button style={btnNav} onClick={() => navigate('/sales')}>💵 Punto de Venta</button>
          )}
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>🚪 Salir</button>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

      {/* Alerta de stock bajo */}
      {Array.isArray(products) && products.filter(p => p && p.stock > 0 && p.stock <= 1).length > 0 && (
        <div className="stack-sm" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span style={{ fontSize: '14px', color: '#856404', flex: 1 }}>
            <strong>{products.filter(p => p && p.stock > 0 && p.stock <= 1).length}</strong> producto(s) con stock bajo (≤1 unidad)
          </span>
          <button
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              background: isSubscriptionExpired ? '#cbd5e0' : '#ffc107',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              color: isSubscriptionExpired ? '#666' : 'inherit',
              opacity: isSubscriptionExpired ? 0.7 : 1
            }}
            onClick={() => isSubscriptionExpired ? toast.error('Función bloqueada: suscripción vencida.') : navigate('/reports')}
            title={isSubscriptionExpired ? "Suscripción vencida - Bloqueado" : ""}
          >
            Ver detalles
          </button>
        </div>
      )}

      {/* Panel de productos por categoría */}
      {Array.isArray(products) && products.length > 0 && (() => {
        const categoryCounts = {};
        products.forEach(p => {
          const cat = p.category || 'Sin categoría';
          categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
        });
        const sortedCategories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);

        return (
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '15px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <strong style={{ fontSize: '14px', color: '#495057' }}>
                Productos por Categoría ({sortedCategories.length} categorías)
              </strong>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '8px',
              maxHeight: '120px',
              overflowY: 'auto',
              padding: '4px'
            }}>
              {sortedCategories.map(([category, count]) => (
                <div
                  key={category}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    marginRight: '8px'
                  }}>
                    {category}
                  </span>
                  <span style={{
                    fontWeight: 'bold',
                    color: '#007bff',
                    minWidth: '30px',
                    textAlign: 'right'
                  }}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Category quick-create modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div style={modalBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }} style={{ ...modalContent, width: isMobile ? '95%' : '520px', maxWidth: '95%' }}>
              <h2 style={{ textAlign: 'center', marginBottom: '12px' }}>➕ Nueva Categoría</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 13 }}>Nombre de la categoría</label>
                <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Ej: BEBIDAS" style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" style={btnGreen} disabled={createCategoryLoading} onClick={handleCreateCategory}>{createCategoryLoading ? 'Creando...' : 'Crear'}</button>
                    <button type="button" style={btnNav} onClick={() => { setNewCategoryName(''); setShowCategoryModal(false); focusSearch(); }}>Cerrar</button>
                  </div>
                  <div />
                </div>
              </div>

              <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12 }}>
                <h3 style={{ margin: '6px 0 10px 0' }}>Categorías existentes</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Array.isArray(categories) && categories.length > 0 ? (
                    categories.map((c) => (
                      <div key={c._id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="text"
                          defaultValue={c.category}
                          onChange={(e) => {
                            setCategoryEdits(prev => ({ ...prev, [c._id]: e.target.value }));
                          }}
                          style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ddd' }}
                        />
                        <button style={btnGreen} onClick={async () => {
                          const id = c._id;
                          const name = (categoryEdits[id] || c.category || '').trim();
                          if (!name) { toast.warn('Nombre vacío'); return; }
                          try {
                            await dispatch(categoryUpdate({ id, body: { name } })).unwrap();
                            await dispatch(getCategories()).unwrap();
                            setCategoryEdits(prev => { const newEdits = { ...prev }; delete newEdits[id]; return newEdits; });
                          } catch (err) { console.error('update cat', err); toast.error('Error actualizando'); }
                        }}>Guardar</button>
                        <button style={btnRed} onClick={async () => {
                          if (!window.confirm('Eliminar categoría? Esta acción no se puede deshacer.')) return;
                          try { await dispatch(categoryDelete(c._id)).unwrap(); await dispatch(getCategories()).unwrap(); } catch (err) { console.error('delete cat', err); toast.error('Error eliminando'); }
                        }}>Eliminar</button>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#666' }}>No hay categorías aún.</div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Supplier quick-create modal */}
      <AnimatePresence>
        {showSupplierModal && (
          <motion.div style={{ ...modalBackdrop, zIndex: 100 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }} style={{ ...modalContent, width: isMobile ? '95%' : '520px', maxWidth: '95%' }}>
              <h2 style={{ textAlign: 'center', marginBottom: '12px' }}>🏭 Nuevo Proveedor</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Nombre del proveedor *</label>
                  <input
                    type="text"
                    value={newSupplierData.nombre}
                    onChange={(e) => setNewSupplierData({ ...newSupplierData, nombre: e.target.value })}
                    placeholder="Ej: Distribuidora ABC"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>CUIT/DNI (opcional)</label>
                  <input
                    type="text"
                    value={newSupplierData.docNro}
                    onChange={(e) => setNewSupplierData({ ...newSupplierData, docNro: e.target.value })}
                    placeholder="Ej: 20-12345678-9"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Email (opcional)</label>
                  <input
                    type="email"
                    value={newSupplierData.email}
                    onChange={(e) => setNewSupplierData({ ...newSupplierData, email: e.target.value })}
                    placeholder="proveedor@ejemplo.com"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Teléfono (opcional)</label>
                  <input
                    type="tel"
                    value={newSupplierData.telefono}
                    onChange={(e) => setNewSupplierData({ ...newSupplierData, telefono: e.target.value })}
                    placeholder="011 1234-5678"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                  <button type="button" style={btnNav} onClick={() => { setNewSupplierData({ nombre: '', docNro: '', email: '', telefono: '' }); setShowSupplierModal(false); focusSearch(); }}>Cancelar</button>
                  <button type="button" style={btnGreen} disabled={createSupplierLoading} onClick={handleCreateSupplier}>{createSupplierLoading ? 'Creando...' : 'Crear Proveedor'}</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal DEV: Eliminar por Categoría */}
      <AnimatePresence>
        {showDeleteByCategoryModal && (
          <motion.div style={modalBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowDeleteByCategoryModal(false)}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              style={{ ...modalContent, width: isMobile ? '95%' : '520px', maxWidth: '95%', background: '#fff5f5', border: '2px solid #dc3545' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ textAlign: 'center', marginBottom: '12px', color: '#dc3545' }}>⚠️ DEV: Eliminar Productos por Categoría</h2>
              <div style={{ background: '#fff3cd', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #ffc107' }}>
                <strong style={{ color: '#856404' }}>⚠️ ADVERTENCIA:</strong>
                <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
                  Esta acción eliminará TODOS los productos de la categoría especificada y NO se puede deshacer.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4, fontWeight: 'bold' }}>Nombre de la Categoría</label>
                  <input
                    type="text"
                    value={deleteCategoryName}
                    onChange={(e) => setDeleteCategoryName(e.target.value)}
                    placeholder="Ej: BEBIDAS"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                  {deleteCategoryName && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                      Productos encontrados: {products.filter(p => (p.category || '').toUpperCase() === deleteCategoryName.trim().toUpperCase()).length}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4, fontWeight: 'bold' }}>Escribe "ELIMINAR" para confirmar</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="ELIMINAR"
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                  <button type="button" style={btnNav} onClick={() => {
                    setShowDeleteByCategoryModal(false);
                    setDeleteCategoryName('');
                    setDeleteConfirmText('');
                  }}>Cancelar</button>
                  <button
                    type="button"
                    style={{ ...btnRed, opacity: deleteConfirmText === 'ELIMINAR' ? 1 : 0.5 }}
                    disabled={deleteConfirmText !== 'ELIMINAR'}
                    onClick={handleDeleteByCategory}
                  >
                    🗑️ Eliminar Productos
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }} className="stack-sm">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="🔍 Buscar producto por nombre, categoría o código de barras..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputSearch}
        />
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: isMobile ? '10px' : '0' }}>
          {!isRestaurant && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
              <input
                type="checkbox"
                checked={filterOnline}
                onChange={(e) => setFilterOnline(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              🌐 Solo Tienda Online
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>
            <input
              type="checkbox"
              checked={showCostMargin}
              onChange={(e) => setShowCostMargin(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            💰 Mostrar Costo/Margen
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} className="stack-sm">
          <button style={btnGreen} onClick={() => {
            setFormData({
              _id: '', name: '', category: '', stock: '', costPrice: '', margin: '', salePrice: '', barcode: '', alicuotaIVACode: discriminarIVA ? 5 : 0, noControlStock: false, onSale: false, variants: []
            });
            setIsManualCategory(false);
            setEditMode(false);
            setShowModal(true);
          }}>➕ Nuevo Producto</button>
          <button
            style={{
              ...btnNav,
              background: isSubscriptionExpired ? '#cbd5e0' : '#17a2b8',
              cursor: isSubscriptionExpired ? 'not-allowed' : 'pointer',
              opacity: isSubscriptionExpired ? 0.7 : 1
            }}
            onClick={isSubscriptionExpired ? () => toast.error('Función bloqueada: suscripción vencida. Renueva para exportar.') : handleExport}
            disabled={isSubscriptionExpired}
            title={isSubscriptionExpired ? "Suscripción vencida - Exportación deshabilitada" : "Exportar a Excel"}
          >
            📥 Exportar Excel
          </button>
          <button style={{ ...btnNav, background: uploading ? '#b0b3b5' : '#6c757d' }} onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading}>📤 Importar Excel</button>
          <button style={{ ...btnNav, background: '#ffc107', color: '#000' }} onClick={handleDownloadTemplate}>📄 Descargar plantilla</button>
          <button style={{ ...btnNav, background: '#28a745' }} onClick={() => { setNewCategoryName(''); setShowCategoryModal(true); }}>➕ Categoría</button>
          <button style={{ ...btnNav, background: '#6f42c1' }} onClick={() => {
            // reset bulk modal fields each time it's opened
            setBulkCategory('');
            setBulkPctCost('');
            setBulkPctMargin('');
            setBulkPctSale('');
            setBulkNoModifyStock(false);
            setBulkApplying(false);
            setBulkProgress({ done: 0, total: 0 });
            setBulkCancelRequested(false);
            bulkCancelRequestedRef.current = false;
            setShowBulkModal(true);
          }}>🔧 Ajuste masivo</button>

          <button
            style={{ ...btnNav, background: '#e83e8c', boxShadow: '0 0 10px rgba(232, 62, 140, 0.2)' }}
            onClick={() => setShowPhotoMode(true)}
            title="Escáner + Fotos (Modo Rápido)"
          >
            📸 Modo Foto
          </button>

          <button
            style={{ ...btnNav, background: '#17a2b8', boxShadow: '0 0 5px rgba(23,162,184,0.3)' }}
            onClick={() => {
              setViewingQueue(false);
              setShowLabelModal(true);
            }}
            title="Abrir escáner de etiquetas rápidas"
          >
            🖨️ Etiquetas
          </button>

          {/* 
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => {
            setDeleteCategoryName('');
            setDeleteConfirmText('');
            setShowDeleteByCategoryModal(true);
          }}>🗑️ DEV: Eliminar Categoría</button>
          */}

          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
      </div>

      {/* Upload progress indicator: muestra bytes/% mientras sube, luego animación durante procesado y botón cancelar */}
      {uploadPhase !== 'idle' && (
        <div style={{ marginBottom: '12px', width: '100%', maxWidth: '800px' }}>
          <div style={{ height: '10px', background: '#e9ecef', borderRadius: 6, overflow: 'hidden' }}>
            {uploadPhase === 'processing' ? (
              <div style={{ width: `${uploadProgress}%`, height: '100%', transition: 'width 400ms linear' }}>
                <div className="processing-stripes" style={{ height: '100%', width: '100%' }} />
              </div>
            ) : (
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: uploadPhase === 'done' ? '#007bff' : '#28a745', transition: 'width 200ms ease' }} />
            )}
          </div>
          <div style={{ marginTop: '6px', fontSize: '13px', color: '#333' }}>
            {uploadPhase === 'uploading' && (
              (() => {
                if (uploadTotal) {
                  const remainingPct = Math.max(0, 100 - uploadProgress);
                  const remainingBytes = uploadTotal - uploadLoaded;
                  if (uploadLoaded < uploadTotal) {
                    return `${uploadProgress}% — ${formatBytes(uploadLoaded)} / ${formatBytes(uploadTotal)} subiendo... (Falta ${remainingPct}% • ${formatBytes(remainingBytes)} restantes)`;
                  }
                  return 'Procesando en servidor…';
                }
                return `${formatBytes(uploadLoaded)} subiendo...`;
              })()
            )}
            {uploadPhase === 'processing' && (
              (() => {
                const dp = getDisplayProgress();
                return (
                  <span>
                    {dp}% — Procesando en servidor{processingEta ? ` • aprox ${formatDuration(processingEta)}` : '…'}
                  </span>
                );
              })()
            )}
            {uploadPhase === 'done' && (
              <span>✅ Subida completada</span>
            )}
          </div>
          <div style={{ marginTop: '8px' }} />
        </div>
      )}

      {/* Blocking overlay: evita interacciones con la UI mientras hay una importación en curso,
                pero mantiene disponible el botón de cancelar. Se muestra tanto en 'uploading' como en 'processing'. */}
      {(uploadPhase === 'uploading' || uploadPhase === 'processing') && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99998, pointerEvents: 'auto' }}>
          <div style={{ background: '#fff', padding: '18px', borderRadius: 10, minWidth: 320, maxWidth: '90%', boxShadow: '0 6px 18px rgba(0,0,0,0.2)', textAlign: 'center' }}>
            <div style={{ fontWeight: '600', marginBottom: 8 }}>
              {uploadPhase === 'uploading' ? `Subiendo — ${uploadProgress}%` : 'Procesando en servidor…'}
            </div>
            <div style={{ height: 10, background: '#e9ecef', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
              {uploadPhase === 'uploading' ? (
                <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#28a745', transition: 'width 200ms ease' }} />
              ) : (
                <div style={{ width: `${uploadProgress}%`, height: '100%', transition: 'width 400ms linear' }}>
                  <div className="processing-stripes" style={{ height: '100%', width: '100%' }} />
                </div>
              )}
            </div>
            <div style={{ marginBottom: 12, color: '#444', fontSize: 13 }}>
              {uploadPhase === 'uploading' ? (
                uploadTotal ? `${formatBytes(uploadLoaded)} / ${formatBytes(uploadTotal)} (Faltan ${Math.max(0, 100 - uploadProgress)}%)` : `${formatBytes(uploadLoaded)} subiendo...`
              ) : (
                uploadPhase === 'processing' ? (
                  (() => {
                    const dp = getDisplayProgress();
                    return processingEta ? `Procesando en servidor… • aprox ${formatDuration(processingEta)}` : `El servidor está procesando el archivo. Esto puede tardar unos segundos. (${dp}%)`;
                  })()
                ) : (
                  'El servidor está procesando el archivo. Esto puede tardar unos segundos.'
                )
              )}
            </div>
            <div>
              <button type="button" onClick={handleCancelImport} style={{ ...btnRed, padding: '8px 12px' }}>⏹️ Cancelar importación</button>
            </div>
          </div>
        </div>
      )}

      {/* Removed duplicate floating cancel button to avoid two cancel controls; keep the button inside the overlay */}


      {useMemo(() => (<>
        {/* Vista móvil: tarjetas */}
        <div className="show-on-mobile" style={{ display: 'grid', gap: 12 }}>
          {pagedProducts.map((p) => (
            <div key={p._id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    {p.isCombo && (
                      <div style={{ background: '#ffc107', color: '#000', fontSize: 12, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>Combo</div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>{p.category} · {p.barcode}</div>
                  {p.supplierId?.nombre && <div style={{ fontSize: 11, color: '#007bff' }}>Prov: {p.supplierId.nombre}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <label style={{ fontSize: 12, color: '#333', display: 'flex', gap: 8, alignItems: 'center' }} title="Permitir venta aunque no haya stock">
                    <input
                      type="checkbox"
                      checked={!!p.noControlStock}
                      onChange={(e) => handleToggleNoControl(p, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span style={{ fontSize: 12 }}>{p.noControlStock ? 'Sin Stock' : 'Stock Ctrl'}</span>
                  </label>
                  {!isRestaurant && (
                    <label style={{ fontSize: 12, color: '#333', display: 'flex', gap: 8, alignItems: 'center' }} title="Producto visible en la tienda online">
                      <input
                        type="checkbox"
                        checked={!!p.sellOnline}
                        onChange={(e) => handleToggleSellOnline(p, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span style={{ fontSize: 12 }}>{p.sellOnline ? 'En Tienda' : 'Oculto'}</span>
                    </label>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700 }}>
                    ${(
                      showFinalPriceWithIVA
                        ? (p.alicuotaIVACode === 0
                          ? p.salePrice
                          : p.salePrice * (1 + getAlicuotaPercent(p.alicuotaIVACode ?? p.alicuotaIVA ?? 5) / 100))
                        : p.salePrice
                    ).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: p.stock < 5 ? 'red' : '#666' }}>Stock: {p.stock}</div>
                </div>
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button style={btnNav} onClick={() => handleEdit(p)}>✏️ Editar</button>
                <button style={btnRed} onClick={() => handleDelete(p._id)}>🗑️ Eliminar</button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 10 }}>No se encontraron productos.</div>
          )}
        </div>

        {/* Print Queue Button */}
        {printQueueIds.length > 0 && (
          <button
            onClick={handleOpenQueue}
            style={{
              backgroundColor: '#6f42c1',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            🏷️ Cola de Etiquetas ({printQueueIds.length})
          </button>
        )}

        {/* Grilla Excel (escritorio) */}
        <div className="responsive-table hide-on-mobile">
          <table style={table}>
            <thead style={{ backgroundColor: '#007bff', color: 'white' }}>
              <tr>
                <th style={{ ...th, width: '40px', textAlign: 'center' }}>
                  <input type="checkbox" onChange={handleSelectAll} checked={filtered.length > 0 && selectedProductIds.size === filtered.length} />
                </th>
                <th style={{ ...th, width: 48 }}>Foto</th>
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('name')}>Nombre {sortBy === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('category')}>Categoría {sortBy === 'category' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('barcode')}>Código {sortBy === 'barcode' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={th}>Proveedor</th>
                <th style={th}>Permitir venta</th>
                {!isRestaurant && <th style={th}>Tienda</th>}
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('stock')}>Stock {sortBy === 'stock' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                {showCostMargin && <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('costPrice')}>Costo {sortBy === 'costPrice' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>}
                {showCostMargin && <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('margin')}>Margen {sortBy === 'margin' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>}
                {discriminarIVA && <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('alicuotaIVACode')}>IVA {sortBy === 'alicuotaIVACode' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>}
                <th style={th}>Descuento</th>
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('salePrice')}>Precio {sortBy === 'salePrice' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ ...th, cursor: 'pointer' }} onClick={() => handleSort('updatedAt')}>Modificado {sortBy === 'updatedAt' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
              </tr>
            </thead>
            <tbody>
              {pagedProducts.map((p) => (
                <tr
                  key={p._id}
                  style={{ borderBottom: '1px solid #ccc', cursor: 'pointer', backgroundColor: selectedProductIds.has(p._id) ? '#f0f9ff' : 'white' }}
                  onClick={(e) => {
                    if (e.target.type === 'checkbox') return;
                    handleEdit(p);
                  }}
                >
                  <td style={{ ...td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedProductIds.has(p._id)} onChange={() => handleSelectProduct(p._id)} />
                  </td>
                  <td style={{ ...td, width: 48, padding: '4px 6px' }} onClick={e => e.stopPropagation()}>
                    {(() => {
                      const all = [p.image, ...(p.ecommerceImages || [])].filter(img => img && !img.startsWith('http'));
                      const img = all[0];
                      return img ? (
                        <img
                          src={resolveImageUrl(img, 100)}
                          alt={p.name}
                          title="Click para ver en grande"
                          onClick={() => { setPreviewImage(resolveImageUrl(img, 800)); setShowPreviewLightbox(true); }}
                          style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', border: '1px solid #e2e8f0', display: 'block' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null;
                    })()}
                    {(() => {
                      const all = [p.image, ...(p.ecommerceImages || [])].filter(img => img && !img.startsWith('http'));
                      return (all.length === 0) ? (
                        <div style={{ width: 38, height: 38, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#ccc' }}>📦</div>
                      ) : (
                        <div style={{ width: 38, height: 38, borderRadius: 6, background: '#f0f0f0', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#ccc' }}>📦</div>
                      );
                    })()}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      {p.isCombo && (
                        <div style={{ background: '#ffc107', color: '#000', fontSize: 12, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>Combo</div>
                      )}
                    </div>
                  </td>
                  <td style={td}>{p.category}</td>
                  <td style={td}>{p.barcode}</td>
                  <td style={td}>{p.supplierId?.nombre || '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!p.noControlStock}
                      onChange={(e) => handleToggleNoControl(p, e)}
                      onClick={(e) => e.stopPropagation()}
                      title="Permitir venta aunque no haya stock"
                    />
                  </td>
                  {!isRestaurant && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!p.sellOnline}
                        onChange={(e) => handleToggleSellOnline(p, e)}
                        onClick={(e) => e.stopPropagation()}
                        title="Visible en tienda online"
                      />
                    </td>
                  )}
                  <td style={{ ...td, color: p.stock < 5 ? 'red' : 'black' }}>{p.stock}</td>
                  {showCostMargin && <td style={td}>${p.costPrice.toFixed(2)}</td>}
                  {showCostMargin && <td style={td}>{Number(p.margin || 0).toFixed(2)}</td>}
                  {discriminarIVA && (
                    <td style={td}>{p.alicuotaIVACode === 0 ? 'Exento' : `${getAlicuotaPercent(p.alicuotaIVACode ?? p.alicuotaIVA ?? 5)}%`}</td>
                  )}
                  <td style={td}>
                    {(!p.discountType || p.discountType === 'none') ? (
                      <span style={{ color: '#666' }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 'bold' }}>
                          {p.discountType === 'percent' && `${Number(p.discountValue || 0)}%`}
                          {p.discountType === 'fixed' && `$${Number(p.discountValue || 0).toFixed(2)}`}
                          {p.discountType === 'tiered' && `Por cantidad`}
                        </span>
                        {p.discountType === 'tiered' && Array.isArray(p.discountTiers) && (
                          <small style={{ color: '#444' }}>{p.discountTiers.map((t) => `${t.minQty}${t.maxQty ? '-' + t.maxQty : '+'}: ${t.type === 'percent' ? t.value + '%' : '$' + t.value}`).join(' · ')}</small>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <div>
                      <div>${(
                        showFinalPriceWithIVA
                          ? (p.alicuotaIVACode === 0
                            ? p.salePrice
                            : p.salePrice * (1 + getAlicuotaPercent(p.alicuotaIVACode ?? p.alicuotaIVA ?? 5) / 100))
                          : p.salePrice
                      ).toFixed(2)}</div>
                      {p.discountType && p.discountType !== 'none' && (() => {
                        const disc = computeDiscountedPrice(p, 1);
                        const ivaPct = showFinalPriceWithIVA ? getAlicuotaPercent(p.alicuotaIVACode ?? p.alicuotaIVA ?? 5) : 0;
                        const discWithIva = showFinalPriceWithIVA && (p.alicuotaIVACode ?? p.alicuotaIVA ?? 5) !== 0 ? disc * (1 + ivaPct / 100) : disc;
                        return (<div style={{ color: '#28a745', fontWeight: 'bold' }}>${discWithIva.toFixed(2)}</div>);
                      })()}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ fontSize: 11, color: '#666' }}>
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={discriminarIVA ? "11" : "10"} style={{ textAlign: 'center', padding: '10px' }}>
                    No se encontraron productos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de paginación */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            marginTop: '25px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                backgroundColor: currentPage === 1 ? '#e9ecef' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Anterior
            </button>
            <span style={{ fontWeight: 'bold' }}>
              Página {currentPage} de {totalPages} ({filtered.length} total)
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                backgroundColor: currentPage === totalPages ? '#e9ecef' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Siguiente
            </button>
          </div>
        )}
      </>),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [pagedProducts, filtered, totalPages, currentPage, sortBy, sortDirection, discriminarIVA, handleSort, handleEdit, handleDelete, handleToggleNoControl, handleToggleSellOnline, computeDiscountedPrice, selectedProductIds, handleSelectAll, handleSelectProduct, printQueueIds, handleOpenQueue, showCostMargin, showFinalPriceWithIVA])}


      <LabelPrinterModal
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setViewingQueue(false);
        }}
        selectedProducts={
          viewingQueue
            ? products.filter(p => printQueueIds.includes(p._id))
            : products.filter(p => selectedProductIds.has(p._id))
        }
        allProducts={products}
        onClearQueue={viewingQueue ? clearPrintQueue : undefined}
      />

      <AnimatePresence>
        {selectedProductIds.size > 0 && (
          <motion.div
            initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
            style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#333', color: 'white', padding: '12px 24px', borderRadius: '50px', display: 'flex', gap: '16px', alignItems: 'center', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', zIndex: 90 }}
          >
            <span>{selectedProductIds.size} seleccionados</span>
            <button onClick={() => setShowLabelModal(true)} style={{ background: '#fff', color: '#333', border: 'none', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Imprimir Etiquetas</button>
            <button onClick={() => setSelectedProductIds(new Set())} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '18px' }}>✖</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            style={modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              // centered wide modal (scale animation)
              initial={{ scale: 1, opacity: 1 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0 }}
              style={{
                ...modalContent,
                // wider centered modal to fit inputs side-by-side
                width: isMobile ? '95%' : '1100px',
                maxWidth: '95%',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '24px',
              }}
            >
              <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
                {editMode ? '✏️ Editar Producto' : '🆕 Nuevo Producto'}
              </h2>
              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '12px',
                  alignItems: 'start',
                }}
              >
                {(() => {
                  const labelMap = {
                    name: 'Nombre',
                    category: 'Categoría',
                    barcode: 'Código (barcode)',
                    stock: 'Stock',
                    costPrice: 'Precio de costo',
                    margin: 'Margen (%)',
                    salePrice: 'Precio de venta (sin IVA)',
                  };
                  const fields = ['name', 'category', 'barcode', 'stock', 'costPrice', 'margin', 'salePrice'];
                  return fields.map((field) => (
                    <div key={field} style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: '14px', marginBottom: '4px' }}>
                        {labelMap[field]}
                      </label>
                      {field === 'category' ? (
                        (Array.isArray(categories) && categories.length > 0 && !isManualCategory) ? (
                          <select
                            value={formData.category || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '__NEW__') {
                                setIsManualCategory(true);
                                setFormData(prev => ({ ...prev, category: '' }));
                              } else {
                                setFormData(prev => ({ ...prev, category: value }));
                              }
                            }}
                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}
                            required
                          >
                            <option value="">— Seleccione categoría —</option>
                            {(() => {
                              const current = (formData.category || '').trim();
                              const exists = categories.some(c => c.category === current);
                              // Check rough match
                              const roughMatch = !exists && current ? categories.find(c => c.category.trim().toLowerCase() === current.toLowerCase()) : null;

                              // If we have a value that is NOT in the list, show it
                              if (current && !exists) {
                                return <option key="current-val" value={current}>{current} {roughMatch ? '(Coincidencia parcial)' : '(No listada)'}</option>;
                              }
                              return null;
                            })()}
                            {Array.isArray(categories) && categories.map(c => (<option key={c._id || c.id || c.category} value={c.category}>{c.category}</option>))}
                            <option value="__NEW__" style={{ fontWeight: 'bold', color: '#007bff' }}>➕ Crear nueva categoría...</option>
                          </select>
                        ) : (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              value={formData.category}
                              onChange={(e) => { const value = e.target.value; setFormData(prev => ({ ...prev, category: value })); }}
                              required
                              placeholder="Escriba el nombre de la nueva categoría"
                              autoFocus={isManualCategory}
                              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', flex: 1 }}
                            />
                            {Array.isArray(categories) && categories.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setIsManualCategory(false)}
                                style={{ padding: '0 10px', borderRadius: '6px', border: '1px solid #ccc', background: '#f0f0f0', cursor: 'pointer' }}
                                title="Volver a seleccionar de la lista"
                              >
                                📋
                              </button>
                            )}
                          </div>
                        )
                      ) : field === 'barcode' ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            value={formData.barcode}
                            onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                            required
                            placeholder="Escanear o generar"
                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, barcode: Date.now().toString() }))}
                            style={{ border: 'none', background: '#28a745', color: '#fff', borderRadius: '6px', padding: '0 10px', cursor: 'pointer', fontSize: '18px' }}
                            title="Generar código automático"
                          >
                            ⚡
                          </button>
                        </div>
                      ) : (
                        <input
                          ref={field === 'name' ? nameInputRef : null}
                          type={['stock', 'costPrice', 'margin', 'salePrice'].includes(field) ? 'number' : 'text'}
                          value={formData[field]}
                          step={['costPrice', 'salePrice'].includes(field) ? '0.01' : undefined}
                          onChange={(e) => {
                            if (['costPrice', 'margin', 'salePrice'].includes(field)) {
                              // pass raw value to handler, which will normalize and round
                              handleAutoCalc(field, e.target.value);
                            } else {
                              const value = e.target.value;
                              setFormData(prev => ({ ...prev, [field]: value }));
                            }
                          }}
                          required={['name', 'category', 'barcode'].includes(field)}
                          readOnly={field === 'stock' && tenant?.settings?.enableProductVariants && formData.variants && formData.variants.length > 0}
                          title={field === 'stock' && tenant?.settings?.enableProductVariants && formData.variants && formData.variants.length > 0 ? "El stock se calcula automáticamente desde las variantes" : ""}
                          style={{
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #ccc',
                            backgroundColor: (field === 'stock' && tenant?.settings?.enableProductVariants && formData.variants && formData.variants.length > 0) ? '#f7fafc' : 'white',
                            color: (field === 'stock' && tenant?.settings?.enableProductVariants && formData.variants && formData.variants.length > 0) ? '#718096' : 'inherit'
                          }}
                        />
                      )}
                    </div>
                  ));
                })()}

                {/* Campo adicional: Precio de venta CON IVA (solo para RI) */}
                {showFinalPriceWithIVA && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: '14px', marginBottom: '4px', color: '#28a745', fontWeight: 'bold' }}>
                      Precio de venta (con IVA)
                      <span style={{ marginLeft: '4px', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                        ({(() => {
                          const ivaCode = formData.alicuotaIVACode ?? 5;
                          const ivaPct = ivaCode === 0 ? 0 : getAlicuotaPercent(ivaCode) || 0;
                          return `IVA ${ivaPct}%`;
                        })()})
                      </span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={(() => {
                        const ivaCode = formData.alicuotaIVACode ?? 5;
                        const ivaPct = ivaCode === 0 ? 0 : getAlicuotaPercent(ivaCode) || 0;
                        const ivaMultiplier = 1 + (ivaPct / 100);
                        return ivaCode === 0 ? formData.salePrice : round2(Number(formData.salePrice || 0) * ivaMultiplier);
                      })()}
                      onChange={(e) => handleSalePriceWithIVA(e.target.value)}
                      placeholder="Ingrese precio con IVA"
                      style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '2px solid #28a745',
                        backgroundColor: '#f0fff4'
                      }}
                    />
                    <small style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                      💡 Puede editar este campo y el precio sin IVA se calculará automáticamente
                    </small>
                  </div>
                )}

                {/* Nota informativa sobre el cálculo de IVA para RI */}
                {showFinalPriceWithIVA && (
                  <div style={{ gridColumn: '1 / -1', marginBottom: 12, padding: '15px', background: '#fff9db', borderRadius: '8px', border: '1px solid #fab005' }}>
                    <div style={{ fontSize: '14px', color: '#856404', marginBottom: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ⚠️ ATENCIÓN: Usted es Responsable Inscripto
                    </div>
                    <div style={{ fontSize: '13px', color: '#856404', lineHeight: '1.5' }}>
                      • El campo <strong>"Precio de venta (sin IVA)"</strong> es el valor <strong>neto</strong> que se usa para impuestos.
                      <br />
                      • El campo <strong>verde</strong> es lo que el cliente pagará finalmente (<strong>Precio Final</strong>).
                      <br />
                      • <strong>Recomendación:</strong> Si desea que un producto cueste un número redondo (ej: $1000), escríbalo en el <strong>campo verde</strong> y deje que el sistema calcule el neto automáticamente.
                    </div>
                  </div>
                )}

                {/* Permitir venta sin stock (noControlStock) */}
                <div style={{ gridColumn: '1 / -1', marginBottom: 6 }}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="Si está activo, permitirá vender aun cuando el stock sea 0 o insuficiente">
                    <input
                      type="checkbox"
                      checked={!!formData.noControlStock}
                      onChange={(e) => setFormData({ ...formData, noControlStock: !!e.target.checked })}
                    />
                    <span style={{ fontSize: 14 }}>{formData.noControlStock ? 'Permitir venta aunque no haya stock' : 'Controlar stock (no permitir venta)'}</span>
                  </label>
                </div>

                {/* Proveedor */}
                <div style={{ gridColumn: '1 / -1', marginBottom: 10 }}>
                  <label style={{ fontSize: '14px', marginBottom: '6px', display: 'block' }}>🏭 Proveedor (opcional)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={formData.supplierId || ''}
                      onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                      style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}
                    >
                      <option value="">— Sin proveedor —</option>
                      {suppliers.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.nombre} {s.docNro ? `(${s.docNro})` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowSupplierModal(true)}
                      style={{ ...btnNav, background: '#28a745', color: '#fff', whiteSpace: 'nowrap' }}
                      title="Crear nuevo proveedor"
                    >
                      ➕ Nuevo
                    </button>
                  </div>
                </div>

                {/* Combo UI: reactivado - permite marcar producto como combo y definir componentes */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: 10, gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <input type="checkbox" checked={!!formData.isCombo} onChange={(e) => setFormData({ ...formData, isCombo: !!e.target.checked })} />
                    <span style={{ fontSize: 14, fontWeight: '700' }}>🎁 Es combo (bundle)</span>
                  </label>

                  {formData.isCombo && (
                    <>
                      <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', padding: 12, borderRadius: 6, marginBottom: 12, color: '#0d47a1' }}>
                        <strong>💡 ¿Cómo funciona?</strong>
                        <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20, fontSize: 13 }}>
                          <li>Al vender este combo, se <strong>descuenta automáticamente el stock de cada componente</strong> (no del combo mismo)</li>
                          <li>Ejemplo: Si vendes 1 combo con "Fernet x1 + Coca Cola x2", se descuentan 1 Fernet y 2 Coca Colas del inventario</li>
                          <li>No puedes usar productos que ya sean combos como componentes</li>
                          <li>El precio del combo se toma del <strong>Precio de venta (con IVA)</strong> de arriba</li>
                        </ul>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 14, marginBottom: 8, fontWeight: 'bold' }}>📦 Componentes del combo</div>
                          {Array.isArray(formData.components) && formData.components.length > 0 ? (
                            formData.components.map((c, idx) => {
                              const selectedProduct = products.find(p => String(p._id) === String(c.productId));
                              // Filtrar productos para el buscador (excluir combos)
                              let availableProducts = products.filter(p => !p.isCombo);

                              // Texto actual del input (o nombre del producto seleccionado si no se está editando)
                              const inputValue = componentSearchTexts[idx] !== undefined
                                ? componentSearchTexts[idx]
                                : (selectedProduct ? `${selectedProduct.name} ($${Number(selectedProduct.salePrice || 0).toFixed(2)})` : '');

                              // Filtrar resultados
                              if (inputValue && inputValue !== (selectedProduct ? `${selectedProduct.name} ($${Number(selectedProduct.salePrice || 0).toFixed(2)})` : '')) {
                                const lowerInput = inputValue.toLowerCase();
                                availableProducts = availableProducts.filter(p =>
                                  p.name.toLowerCase().includes(lowerInput) ||
                                  p.barcode.includes(inputValue)
                                );
                              } else if (!inputValue) {
                                availableProducts = availableProducts.slice(0, 50); // Mostrar top 50 si vacío
                              } else {
                                // Coincidencia exacta (producto seleccionado), ocultar menú
                                availableProducts = [];
                              }

                              const showDropdown = componentSearchTexts[idx] !== undefined && availableProducts.length > 0;

                              return (
                                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, padding: 10, background: '#f8f9fa', borderRadius: 6, border: '1px solid #dee2e6', overflow: 'visible', zIndex: 10 + idx }}>
                                  <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>

                                    <input
                                      type="text"
                                      placeholder="🔍 Escribe para buscar producto..."
                                      value={inputValue}
                                      onChange={(e) => {
                                        const newText = e.target.value;
                                        setComponentSearchTexts({ ...componentSearchTexts, [idx]: newText });
                                        if (!newText) handleComponentChange(idx, 'productId', '');
                                      }}
                                      onFocus={() => {
                                        if (componentSearchTexts[idx] === undefined) {
                                          setComponentSearchTexts({ ...componentSearchTexts, [idx]: inputValue });
                                        }
                                      }}
                                      onBlur={() => {
                                        // Delay para permitir click en opciones
                                        setTimeout(() => {
                                          setComponentSearchTexts(prev => {
                                            const newState = { ...prev };
                                            delete newState[idx];
                                            return newState;
                                          });
                                        }, 200);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: 10,
                                        borderRadius: 6,
                                        border: '1px solid #ccc',
                                        fontSize: 14
                                      }}
                                    />

                                    {showDropdown && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        background: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '0 0 6px 6px',
                                        zIndex: 1000,
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                      }}>
                                        {availableProducts.map(p => (
                                          <div
                                            key={p._id}
                                            onClick={() => {
                                              handleComponentChange(idx, 'productId', p._id);
                                              setComponentSearchTexts(prev => {
                                                const newState = { ...prev };
                                                delete newState[idx];
                                                return newState;
                                              });
                                            }}
                                            style={{
                                              padding: '8px 12px',
                                              cursor: 'pointer',
                                              borderBottom: '1px solid #eee',
                                              fontSize: '13px'
                                            }}
                                            onMouseEnter={(e) => e.target.style.background = '#f0f9ff'}
                                            onMouseLeave={(e) => e.target.style.background = 'white'}
                                          >
                                            <div style={{ fontWeight: 'bold' }}>{p.name}</div>
                                            <div style={{ color: '#666', fontSize: '12px' }}>
                                              ${Number(p.salePrice || 0).toFixed(2)} · Stock: {p.stock}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {selectedProduct && !showDropdown && (
                                      <div style={{ fontSize: 11, color: selectedProduct.stock < 5 ? '#dc3545' : '#6c757d', marginTop: 4 }}>
                                        Stock disponible: <strong>{selectedProduct.stock}</strong> unidades
                                      </div>
                                    )}
                                    {/* Selector de variante si aplica */}
                                    {selectedProduct && selectedProduct.variants && selectedProduct.variants.length > 0 && (
                                      <div style={{ marginTop: 8 }}>
                                        <select
                                          value={c.variantSku || ''}
                                          onChange={(e) => handleComponentChange(idx, 'variantSku', e.target.value)}
                                          style={{
                                            width: '100%',
                                            padding: 6,
                                            borderRadius: 6,
                                            border: '1px solid #17a2b8',
                                            backgroundColor: '#f0fbff',
                                            fontSize: 12
                                          }}
                                        >
                                          <option value="">— Elegir variante (Defecto: Primera) —</option>
                                          {selectedProduct.variants.map((v, vIdx) => (
                                            <option key={vIdx} value={v.sku || `idx-${vIdx}`}>
                                              {v.attributes?.color ? `Color: ${v.attributes.color} ` : ''}
                                              {v.attributes?.size ? `Talle: ${v.attributes.size} ` : ''}
                                              {v.attributes?.style ? `Estrilo: ${v.attributes.style} ` : ''}
                                              {(!v.attributes?.color && !v.attributes?.size && !v.attributes?.style) ? `Variante ${vIdx + 1} ` : ''}
                                              (Stock: {v.stock})
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <label style={{ fontSize: 11, color: '#666' }}>Cantidad</label>
                                    <input
                                      type="number"
                                      min={1}
                                      value={c.qty || 1}
                                      onChange={(e) => handleComponentChange(idx, 'qty', e.target.value)}
                                      style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <label style={{ fontSize: 11, color: '#666' }}>Precio override</label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      placeholder="Opcional"
                                      value={c.priceOverride === '' || c.priceOverride === undefined ? '' : c.priceOverride}
                                      onChange={(e) => handleComponentChange(idx, 'priceOverride', e.target.value)}
                                      style={{ width: 100, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                                    />
                                  </div>
                                  <button type="button" style={{ ...btnRed, whiteSpace: 'nowrap' }} onClick={() => handleRemoveComponent(idx)}>🗑️ Quitar</button>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ color: '#666', marginBottom: 6, padding: 12, background: '#f8f9fa', borderRadius: 6, textAlign: 'center' }}>
                              No hay componentes todavía. Haz clic en "➕ Añadir componente" para empezar.
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: 8, marginTop: 15, flexWrap: 'wrap', alignItems: 'center' }}>
                            <button type="button" style={{ ...btnNav, background: '#28a745' }} onClick={handleAddComponent}>➕ Añadir componente</button>
                            <button
                              type="button"
                              style={{ ...btnNav, background: '#17a2b8' }}
                              onClick={calculateComboCostFromComponents}
                              title="Suma el costo de cada componente para obtener el costo total del combo"
                            >
                              📊 Calcular costo desde componentes
                            </button>
                            <button type="button" style={{ ...btnNav, background: '#ffc107', color: '#000' }} onClick={() => setFormData({ ...formData, components: [], comboPrice: '' })}>🗑️ Borrar todos</button>
                          </div>
                        </div>

                        <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', padding: 10, borderRadius: 6, color: '#856404' }}>
                          <strong>💰 Precio del combo:</strong> El precio del combo se toma automáticamente del campo <strong>"Precio de venta (con IVA)"</strong> que está arriba. No es necesario calcularlo manualmente.
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Descuentos */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '14px', marginBottom: '6px', fontWeight: 'bold' }}>Descuentos (opcional)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <select value={formData.discountType} onChange={(e) => setFormData({ ...formData, discountType: e.target.value })} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}>
                      <option value="none">Ninguno</option>
                      <option value="percent">Porcentaje (%)</option>
                      <option value="fixed">Monto fijo ($ por unidad)</option>
                      <option value="tiered">Por cantidad (rango)</option>
                    </select>
                    {formData.discountType === 'percent' && (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.discountValue}
                        onChange={(e) => {
                          // Allow comma as decimal separator; normalize to dot for internal storage
                          const v = String(e.target.value || '');
                          const normalized = v.replace(/,/g, '.');
                          // keep as string (Number() used elsewhere when needed)
                          setFormData({ ...formData, discountValue: normalized });
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', width: '140px' }}
                        placeholder="%"
                      />
                    )}
                    {formData.discountType === 'fixed' && (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.discountValue}
                        onChange={(e) => {
                          const v = String(e.target.value || '');
                          const normalized = v.replace(/,/g, '.');
                          setFormData({ ...formData, discountValue: normalized });
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc', width: '140px' }}
                        placeholder="$"
                      />
                    )}
                    {formData.discountType === 'tiered' && (
                      <button type="button" style={{ ...btnNav, background: '#17a2b8' }} onClick={() => setFormData({ ...formData, discountTiers: [...(formData.discountTiers || []), { minQty: 1, maxQty: '', type: 'percent', value: '' }] })}>+ Añadir rango</button>
                    )}
                  </div>

                  {formData.discountType === 'tiered' && Array.isArray(formData.discountTiers) && (
                    <div style={{ marginTop: '8px' }}>
                      {formData.discountTiers.map((t, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                          <input type="number" min={1} value={t.minQty} onChange={(e) => {
                            const arr = [...formData.discountTiers]; arr[idx].minQty = Number(e.target.value); setFormData({ ...formData, discountTiers: arr });
                          }} style={{ padding: '6px', width: '80px' }} placeholder="minQty" />
                          <input type="number" min={0} value={t.maxQty} onChange={(e) => {
                            const arr = [...formData.discountTiers]; arr[idx].maxQty = e.target.value === '' ? '' : Number(e.target.value); setFormData({ ...formData, discountTiers: arr });
                          }} style={{ padding: '6px', width: '80px' }} placeholder="maxQty (opcional)" />
                          <select value={t.type} onChange={(e) => { const arr = [...formData.discountTiers]; arr[idx].type = e.target.value; setFormData({ ...formData, discountTiers: arr }); }} style={{ padding: '6px' }}>
                            <option value="percent">%</option>
                            <option value="fixed">$</option>
                          </select>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.value}
                            onChange={(e) => {
                              const arr = [...formData.discountTiers];
                              const v = String(e.target.value || '');
                              const normalized = v.replace(/,/g, '.');
                              // store as string so user can type comma/decimal and keep intermediate state
                              arr[idx].value = normalized === '' ? '' : normalized;
                              setFormData({ ...formData, discountTiers: arr });
                            }}
                            style={{ padding: '6px', width: '100px' }}
                            placeholder="valor"
                          />
                          <button type="button" style={btnRed} onClick={() => { const arr = [...formData.discountTiers]; arr.splice(idx, 1); setFormData({ ...formData, discountTiers: arr }); }}>Eliminar</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Preview */}
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#333' }}>
                    <div>Precio base: <strong>${Number(formData.salePrice || 0).toFixed(2)}</strong></div>
                    <div>Precio con descuento (1 unidad): <strong>${computeDiscountedPrice(formData, 1).toFixed(2)}</strong></div>
                    {formData.discountType === 'tiered' && formData.discountTiers && formData.discountTiers.length > 0 && (
                      <div>Ejemplo con qty={formData.discountTiers[0].minQty}: <strong>${computeDiscountedPrice(formData, Number(formData.discountTiers[0].minQty || 1)).toFixed(2)}</strong></div>
                    )}
                  </div>
                </div>
                {discriminarIVA ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '14px', marginBottom: '4px' }}>ALICUOTA IVA</label>
                    <select value={formData.alicuotaIVACode} onChange={(e) => setFormData({ ...formData, alicuotaIVACode: Number(e.target.value) })} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}>
                      <option value={5}>21% (código AFIP 5)</option>
                      <option value={3}>10.5% (código AFIP 3)</option>
                      <option value={0}>Exento (código AFIP 0)</option>
                    </select>
                  </div>
                ) : null}
                {/* Variantes (Talle y Color) */}
                {tenant?.settings?.enableProductVariants && (
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '14px', marginBottom: '6px', fontWeight: 'bold' }}>Variantes (Talle y Color)</label>
                    <div style={{ background: '#f7fafc', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                      <div style={{ fontSize: 13, marginBottom: 8, color: '#4a5568' }}>Generador rápido (separar con comas)</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: 12, display: 'block' }}>Colores</label>
                          <input type="text" placeholder="Ej: Rojo, Azul, Negro" value={variantMatrix.colors} onChange={e => setVariantMatrix({ ...variantMatrix, colors: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: 12, display: 'block' }}>Talles</label>
                          <input type="text" placeholder="Ej: S, M, L, XL" value={variantMatrix.sizes} onChange={e => setVariantMatrix({ ...variantMatrix, sizes: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                        </div>
                        <button type="button" onClick={handleGenerateVariants} style={{ ...btnNav, background: '#667eea', height: '32px' }}>Generar Variantes</button>
                      </div>
                    </div>

                    {formData.variants && formData.variants.length > 0 && (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                              <th style={{ padding: 6 }}>Color</th>
                              <th style={{ padding: 6 }}>Talle</th>
                              <th style={{ padding: 6 }}>SKU</th>
                              <th style={{ padding: 6 }}>Stock</th>
                              <th style={{ padding: 6, width: 40 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.variants.map((v, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                                <td style={{ padding: 4 }}><input type="text" value={v.attributes?.color || ''} onChange={(e) => handleVariantChange(idx, 'color', e.target.value)} style={{ width: 80, padding: 4, borderRadius: 4, border: '1px solid #e2e8f0' }} /></td>
                                <td style={{ padding: 4 }}><input type="text" value={v.attributes?.size || ''} onChange={(e) => handleVariantChange(idx, 'size', e.target.value)} style={{ width: 60, padding: 4, borderRadius: 4, border: '1px solid #e2e8f0' }} /></td>
                                <td style={{ padding: 4 }}><input type="text" value={v.sku || ''} onChange={(e) => handleVariantChange(idx, 'sku', e.target.value)} style={{ width: 140, padding: 4, borderRadius: 4, border: '1px solid #e2e8f0' }} /></td>
                                <td style={{ padding: 4 }}><input type="number" min={0} value={v.stock} onChange={(e) => handleVariantChange(idx, 'stock', Number(e.target.value))} style={{ width: 70, padding: 4, borderRadius: 4, border: '1px solid #e2e8f0' }} /></td>
                                <td style={{ padding: 4 }}><button type="button" onClick={() => handleRemoveVariant(idx)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#e53e3e' }}>🗑️</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#718096', fontStyle: 'italic' }}>
                          * El stock total del producto se calculará automáticamente sumando las variantes ({formData.variants.reduce((a, b) => a + (Number(b.stock) || 0), 0)} u.).
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Mensajes de validación inline para que el usuario sepa qué falta completar */}
                {(() => {
                  const _errs = getModalValidationErrors();
                  if (!_errs || _errs.length === 0) return null;
                  return (
                    <div style={{ gridColumn: '1 / -1', marginBottom: 8 }}>
                      <div style={{ color: 'red', fontSize: 13 }}>
                        <strong>Corrige los siguientes errores:</strong>
                        <ul style={{ margin: '6px 0 0 18px' }}>
                          {_errs.map((m, i) => (<li key={i}>{m}</li>))}
                        </ul>
                      </div>
                    </div>
                  );
                })()}

                {/* Sección E-commerce Propio */}
                {tenant?.modules?.ecommerce?.ownShop?.enabled && !isRestaurant && (
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', gridColumn: '1 / -1', marginTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '20px' }}>🌐</div>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>E-commerce Propio</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!formData.sellOnline}
                            onChange={(e) => setFormData({ ...formData, sellOnline: !!e.target.checked })}
                          />
                          <span style={{ fontSize: 14, fontWeight: 'bold' }}>Vender online en tienda propia</span>
                        </label>

                        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!formData.onSale}
                            onChange={(e) => setFormData({ ...formData, onSale: !!e.target.checked })}
                          />
                          <span style={{ fontSize: 14, fontWeight: 'bold', color: '#e53e3e' }}>🔥 Producto en oferta (Destacar en tienda)</span>
                        </label>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                          <label style={{ fontSize: 13, color: '#4a5568' }}>Precio E-commerce (Opcional)</label>
                          <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 10, top: 9, color: '#a0aec0' }}>$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.ecommercePrice ?? ''}
                              onChange={(e) => setFormData({ ...formData, ecommercePrice: e.target.value === '' ? null : Number(e.target.value) })}
                              placeholder={`Referencia: $${Number(formData.salePrice || 0).toFixed(2)}`}
                              style={{
                                padding: '8px 8px 8px 25px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e0',
                                fontSize: '14px',
                                width: '100%',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                          <small style={{ fontSize: 11, color: '#718096' }}>Si se deja vacío, usará el precio de venta normal.</small>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ fontSize: 13, color: '#4a5568' }}>Descripción para la web (opcional)</label>
                          <textarea
                            value={formData.ecommerceDescription}
                            onChange={(e) => setFormData({ ...formData, ecommerceDescription: e.target.value })}
                            placeholder="Describe tu producto para atraer clientes online..."
                            rows={4}
                            style={{
                              padding: '10px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e0',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: 13, color: '#4a5568', display: 'block', marginBottom: 8 }}>Fotos del producto (E-commerce)</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="text"
                              placeholder="Pegar URL de imagen (jpg, png...)"
                              id="new-image-url"
                              style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e0' }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.target.value.trim();
                                  if (val) {
                                    setFormData(prev => ({
                                      ...prev,
                                      ecommerceImages: [...prev.ecommerceImages, val]
                                    }));
                                    e.target.value = '';
                                  }
                                }
                              }}
                            />
                            <button
                              type="button"
                              style={{ ...btnNav, background: '#4a5568' }}
                              onClick={() => {
                                const input = document.getElementById('new-image-url');
                                const val = input.value.trim();
                                if (val) {
                                  setFormData(prev => ({
                                    ...prev,
                                    ecommerceImages: [...prev.ecommerceImages, val]
                                  }));
                                  input.value = '';
                                }
                              }}
                            >Agregar URL</button>

                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  type="button"
                                  style={{ ...btnNav, background: '#17a2b8' }}
                                  onClick={() => document.getElementById('ecommerce-image-upload').click()}
                                >📁 Subir Foto</button>
                                <button
                                  type="button"
                                  style={{ ...btnNav, background: '#20c997' }}
                                  onClick={() => document.getElementById('ecommerce-camera-capture').click()}
                                  title="Tomar foto con la cámara del dispositivo"
                                >📷 Cámara</button>
                              </div>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 10, cursor: 'pointer', color: '#2b6cb0', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                <input type="checkbox" checked={removeBgProduct} onChange={(e) => setRemoveBgProduct(e.target.checked)} />
                                🪄 Quitar fondo
                              </label>
                            </div>

                            <button
                              type="button"
                              style={{ ...btnNav, background: '#667eea' }}
                              onClick={() => {
                                setExternalResults([]); // Limpiar resultados anteriores
                                setSearchExternalQuery(formData.name);
                                setShowSearchExternalModal(true);
                                if (formData.name) handleSearchExternal(formData.name);
                              }}
                            >🔍 Buscar en la Web</button>

                            <input
                              type="file"
                              id="ecommerce-image-upload"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                let file = e.target.files[0];
                                if (!file) return;

                                const loadingToast = toast.info(removeBgProduct ? '🤖 IA procesando imagen (esto puede tardar)...' : 'Subiendo imagen...', { autoClose: false });

                                try {
                                  if (removeBgProduct) {
                                    try {
                                      const module = await import('@imgly/background-removal');
                                      const removeBackground = module.default || module.removeBackground;
                                      const blob = await removeBackground(file);
                                      file = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                                      toast.update(loadingToast, { render: '✨ Fondo eliminado. Subiendo al servidor...' });
                                    } catch (iaErr) {
                                      console.error(iaErr);
                                      toast.warn('⚠️ Falló la IA, subiendo original...');
                                    }
                                  }

                                  const fd = new FormData();
                                  fd.append('image', file);
                                  const res = await http.post('/product/upload-image', fd, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setFormData(prev => ({
                                    ...prev,
                                    ecommerceImages: [res.data.imageUrl, ...(prev.ecommerceImages || [])]
                                  }));
                                  toast.dismiss(loadingToast);
                                  toast.success('Imagen subida con éxito');
                                } catch (err) {
                                  toast.dismiss(loadingToast);
                                  toast.error('Error al subir imagen');
                                  console.error(err);
                                }
                                e.target.value = '';
                              }}
                            />

                            {/* Input oculto para captura directa desde cámara (móvil) */}
                            <input
                              type="file"
                              id="ecommerce-camera-capture"
                              accept="image/*"
                              capture="environment"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                let file = e.target.files[0];
                                if (!file) return;

                                const loadingToast = toast.info(removeBgProduct ? '🤖 IA procesando imagen (esto puede tardar)...' : '📷 Subiendo foto...', { autoClose: false });

                                try {
                                  if (removeBgProduct) {
                                    try {
                                      const module = await import('@imgly/background-removal');
                                      const removeBackground = module.default || module.removeBackground;
                                      const blob = await removeBackground(file);
                                      file = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                                      toast.update(loadingToast, { render: '✨ Fondo eliminado. Subiendo al servidor...' });
                                    } catch (iaErr) {
                                      console.error(iaErr);
                                      toast.warn('⚠️ Falló la IA, subiendo original...');
                                    }
                                  }

                                  const fd = new FormData();
                                  fd.append('image', file);
                                  const res = await http.post('/product/upload-image', fd, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  setFormData(prev => ({
                                    ...prev,
                                    ecommerceImages: [res.data.imageUrl, ...(prev.ecommerceImages || [])]
                                  }));
                                  toast.dismiss(loadingToast);
                                  toast.success('📷 Foto subida con éxito');
                                } catch (err) {
                                  toast.dismiss(loadingToast);
                                  toast.error('Error al subir la foto');
                                  console.error(err);
                                }
                                e.target.value = '';
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                            {(formData.ecommerceImages || []).length === 0 && (
                              <div style={{ fontSize: 12, color: '#718096', fontStyle: 'italic' }}>
                                No hay imágenes cargadas. Puedes pegar URLs de imágenes externas o subirlas.
                              </div>
                            )}
                            {(formData.ecommerceImages || []).map((img, idx) => (
                              <div key={idx} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: idx === 0 ? '2px solid #3182ce' : '1px solid #e2e8f0', cursor: 'zoom-in' }}>
                                <img
                                  src={resolveImageUrl(img)}
                                  alt="preview"
                                  title={idx === 0 ? "Imagen Principal" : "Click para ver en grande"}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: idx === 0 ? 1 : 0.8 }}
                                  onClick={() => { setPreviewImage(resolveImageUrl(img)); setShowPreviewLightbox(true); }}
                                />
                                {idx === 0 && (
                                  <div style={{ position: 'absolute', top: 0, left: 0, background: '#3182ce', color: 'white', fontSize: 9, padding: '1px 4px', borderBottomRightRadius: 4, fontWeight: 'bold' }}>PRINCIPAL</div>
                                )}
                                <button
                                  type="button"
                                  title="Eliminar imagen"
                                  style={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    background: 'rgba(229, 62, 62, 0.9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: 22,
                                    height: 22,
                                    fontSize: 10,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('¿Eliminar esta imagen?')) {
                                      setFormData(prev => ({
                                        ...prev,
                                        ecommerceImages: prev.ecommerceImages.filter((_, i) => i !== idx)
                                      }));
                                    }
                                  }}
                                >✕</button>
                                {idx !== 0 && (
                                  <button
                                    type="button"
                                    title="Establecer como principal"
                                    style={{
                                      position: 'absolute',
                                      bottom: 2,
                                      right: 2,
                                      background: 'rgba(255, 255, 255, 0.9)',
                                      color: '#3182ce',
                                      border: '1px solid #3182ce',
                                      borderRadius: '50%',
                                      width: 22,
                                      height: 22,
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData(prev => {
                                        const newImages = [...prev.ecommerceImages];
                                        const [selected] = newImages.splice(idx, 1);
                                        return {
                                          ...prev,
                                          ecommerceImages: [selected, ...newImages]
                                        };
                                      });
                                    }}
                                  >⭐</button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="submit" style={btnGreen} disabled={!isModalValid()}>{editMode ? 'Actualizar' : 'Guardar'}</button>
                    {editMode && (
                      <button
                        type="button"
                        onClick={() => { handleDelete(formData._id); setShowModal(false); }}
                        style={btnRed}
                      >
                        Eliminar
                      </button>
                    )}
                    {/* Removed: Vender combo shortcut - combos are defined in Products but selling combos is handled automatically in Sales */}
                  </div>
                  <button type="button" onClick={() => { setShowModal(false); focusSearch(); }} style={btnRed}>Cancelar</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )
        }
      </AnimatePresence >
      {/* Ajuste masivo modal (placeholder) */}
      < AnimatePresence >
        {showBulkModal && (
          <motion.div
            style={modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              style={{ ...modalContent, width: isMobile ? '95%' : '1100px', maxWidth: '98%', maxHeight: '92vh', overflowY: 'auto' }}
            >
              <h2 style={{ textAlign: 'center', marginBottom: '12px' }}>🔧 Ajuste masivo</h2>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ minWidth: 140 }}>Categoría (opcional)</label>
                  {Array.isArray(categories) && categories.length > 0 ? (
                    <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
                      <option value="">— Todas las categorías —</option>
                      {categories.map((c) => (
                        <option key={c._id || c.id || c.category} value={c.category}>{String(c.category).split('-').join(' ')}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} placeholder="Ej: Bebidas" style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ minWidth: 140 }}>Ajuste % costo</label>
                  <input type="text" inputMode="decimal" value={bulkPctCost} onChange={(e) => setBulkPctCost(e.target.value)} style={{ width: 120, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  <label style={{ minWidth: 140 }}>Ajuste % margen</label>
                  <input type="text" inputMode="decimal" value={bulkPctMargin} onChange={(e) => setBulkPctMargin(e.target.value)} style={{ width: 120, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  <label style={{ minWidth: 140 }}>Ajuste % precio</label>
                  <input type="text" inputMode="decimal" value={bulkPctSale} onChange={(e) => setBulkPctSale(e.target.value)} style={{ width: 120, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ minWidth: 140 }}></label>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} title="Si está activo, permitirá vender aun cuando el stock sea 0 o insuficiente">
                    <input type="checkbox" checked={bulkNoModifyStock} onChange={(e) => setBulkNoModifyStock(!!e.target.checked)} />
                    Permitir venta aunque no haya stock (no controlar stock)
                  </label>
                </div>

                {tenant?.modules?.ecommerce?.ownShop?.enabled && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ minWidth: 140 }}>Tienda Propia</label>
                    <select
                      value={bulkEcommerceAction}
                      onChange={(e) => setBulkEcommerceAction(e.target.value)}
                      style={{ width: 220, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    >
                      <option value="">— No modificar —</option>
                      <option value="enable">✅ Habilitar venta online</option>
                      <option value="disable">🚫 Quitar de venta online</option>
                    </select>
                  </div>
                )}

                {/* Campo para cambiar IVA masivamente */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ minWidth: 140, fontWeight: 'bold' }}>Cambiar IVA</label>
                  <select
                    value={bulkIVACode}
                    onChange={(e) => setBulkIVACode(e.target.value)}
                    style={{ width: 220, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">— No modificar —</option>
                    <option value="0">0% - Exento</option>
                    <option value="3">10.5% - IVA Reducido</option>
                    <option value="4">27% - IVA Incrementado</option>
                    <option value="5">21% - IVA General</option>
                    <option value="6">2.5% - IVA Mínimo</option>
                    <option value="8">5% - IVA Reducido Especial</option>
                    <option value="9">0% - No Gravado</option>
                  </select>
                  <small style={{ fontSize: 11, color: '#666' }}>
                    Se aplicará a todos los productos seleccionados
                  </small>
                </div>

                <div style={{ marginTop: 6, fontSize: 13, color: '#444' }}>
                  <div>Vista previa: se aplicarán los porcentajes a los productos {bulkCategory ? `en la categoría "${bulkCategory}"` : 'de todas las categorías'}.</div>
                  <div style={{ overflowX: 'auto', maxHeight: '42vh', paddingRight: 8 }}>
                    <PreviewBulk products={products} category={bulkCategory} pctCost={Number(bulkPctCost) || 0} pctMargin={Number(bulkPctMargin) || 0} pctSale={Number(bulkPctSale) || 0} noModifyStock={bulkNoModifyStock} />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...btnGreen, opacity: bulkApplying ? 0.6 : 1 }} disabled={bulkApplying} onClick={async () => {
                      try {
                        const candidates = products.filter(p => {
                          if (!p) return false;
                          if (bulkCategory && String(bulkCategory || '').trim() !== '') {
                            return String(p.category || '').toLowerCase() === String(bulkCategory || '').toLowerCase();
                          }
                          return true;
                        });
                        if (!candidates || candidates.length === 0) { toast.info('No hay productos para actualizar'); return; }
                        if (!window.confirm(`Aplicar ajustes a ${candidates.length} producto(s)? Esta acción sobrescribirá costo, margen y precio.`)) return;
                        setBulkCancelRequested(false);
                        bulkCancelRequestedRef.current = false;
                        setBulkApplying(true);
                        setBulkProgress({ done: 0, total: candidates.length });
                        const BULK_THRESHOLD = 20;
                        let _serverTried = false;
                        if (candidates.length >= BULK_THRESHOLD) {
                          _serverTried = true;
                          try {
                            const body = {
                              category: bulkCategory && bulkCategory.trim() !== '' ? bulkCategory : undefined,
                              pctCost: Number(bulkPctCost) || 0,
                              pctMargin: Number(bulkPctMargin) || 0,
                              pctSale: Number(bulkPctSale) || 0,
                              noControlStock: !!bulkNoModifyStock,
                              ecommerceAction: bulkEcommerceAction || undefined,
                              alicuotaIVACode: bulkIVACode ? Number(bulkIVACode) : undefined,
                            };
                            const resp = await http.post('/product/bulk-update', body);
                            toast.success(`✅ Bulk update aplicado. Matched: ${resp.data.matched}, Modified: ${resp.data.modified}`);
                            await fetchProducts();
                            setShowBulkModal(false);
                            return;
                          } catch (err) {
                            // log and fallthrough to client-side background job
                            try {
                              const cfg = err?.config || {};
                              const url = cfg.url || cfg.baseURL ? `${cfg.baseURL || ''}${cfg.url || ''}` : cfg.url;
                              const method = (cfg.method || 'POST').toUpperCase();
                              const status = err?.response?.status;
                              const respData = err?.response?.data;
                              console.error('bulk update server error', { url, method, status, data: respData, message: err?.message });
                            } catch (_) { }
                            toast.warn('No se pudo aplicar bulk en servidor, iniciando procesado en background...');
                          }
                        }

                        // If server wasn't tried (small set) or server failed, start client-side background job
                        try {
                          const ids = candidates.map(p => p._id).filter(Boolean);
                          // persist local overrides immediately so Sales UI allows selling while job runs
                          try { setLocalNoControlForIds(ids, !!bulkNoModifyStock); } catch (_) { }
                          await startClientBulkJob({ candidateIds: ids, startIndex: 0, pctCost: Number(bulkPctCost) || 0, pctMargin: Number(bulkPctMargin) || 0, pctSale: Number(bulkPctSale) || 0, noModifyStock: !!bulkNoModifyStock, ecommerceAction: bulkEcommerceAction, alicuotaIVACode: bulkIVACode ? Number(bulkIVACode) : undefined });
                          toast.info('🔁 Ajuste masivo iniciado en background');
                        } catch (bgErr) {
                          console.error('background bulk start error', bgErr);
                          toast.error('No se pudo iniciar el ajuste en background');
                        }
                      } catch (e) {
                        console.error('bulk apply error', e);
                        toast.error('Error aplicando ajuste masivo');
                      } finally {
                        setBulkApplying(false);
                        setBulkProgress({ done: 0, total: 0 });
                        setBulkCancelRequested(false);
                        bulkCancelRequestedRef.current = false;
                        setShowBulkModal(false);
                      }
                    }}>Aplicar ajustes</button>
                    <button style={{ ...btnNav, background: '#ffc107', color: '#000' }} onClick={() => {
                      if (bulkApplying) {
                        if (!bulkCancelRequested) {
                          setBulkCancelRequested(true);
                          bulkCancelRequestedRef.current = true;
                          toast.info('Solicitud de cancelación enviada...');
                        }
                      } else {
                        setShowBulkModal(false);
                        focusSearch();
                      }
                    }}>{bulkApplying ? 'Cancelar' : 'Cancelar'}</button>
                    <button style={{ ...btnNav, background: '#6c757d' }} disabled={bulkApplying} onClick={() => { setShowBulkModal(false); focusSearch(); }}>Cerrar</button>
                    {!bulkApplying && (
                      <button
                        type="button"
                        style={{ ...btnRed, marginLeft: '24px' }}
                        onClick={handleDeleteAllProducts}
                      >
                        🗑️ Eliminar todo
                      </button>
                    )}
                  </div>
                  <div style={{ minWidth: 260, textAlign: 'right' }}>
                    {bulkApplying ? (<span>Procesando {bulkProgress.done}/{bulkProgress.total}</span>) : (<span>Listo para aplicar</span>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence >
      {/* Import errors modal */}
      {
        importErrors && importErrors.length > 0 && (
          <div style={modalOverlay} onClick={clearImportErrors}>
            <div style={{ ...modalContent, maxWidth: '800px', width: '90%', maxHeight: '70vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h2>Errores durante la importación</h2>
              <p>Algunas filas no pudieron importarse. Revisa los motivos y corrige el archivo.</p>
              <div style={{ marginTop: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Fila</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Motivo</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Contenido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importErrors.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px' }}>{idx + 1}</td>
                        <td style={{ padding: '8px' }}>{it.message || JSON.stringify(it)}</td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#333' }}><pre style={{ margin: 0 }}>{JSON.stringify(it.row || it, null, 2)}</pre></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button style={{ ...btnNav, background: '#6c757d' }} onClick={clearImportErrors}>Cerrar</button>
              </div>
            </div>
          </div>
        )
      }
      {/* Global bulk progress panel (client-side background jobs) */}
      <BulkProgressPanel
        job={activeBulkJob}
        onPause={(id) => pauseJob(id)}
        onResume={(id) => resumeJob(id)}
        onCancel={(id) => cancelJob(id)}
      />

      {/* Modal Ingreso Rápido (F3) */}
      <AnimatePresence>
        {showQuickStockModal && (
          <motion.div
            style={modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowQuickStockModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              style={{
                ...modalContent,
                width: isMobile ? '95%' : '450px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.5) inset',
                padding: '28px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ fontSize: 28 }}>⚡</div>
                <h2 style={{ margin: 0, color: '#1a202c', fontSize: 22, fontWeight: 800 }}>Ingreso Rápido (F3)</h2>
              </div>

              <form onSubmit={handleQuickStockSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', display: 'block', marginBottom: 6 }}>
                    Escanear o ingresar Código
                  </label>
                  <input
                    ref={quickStockInputRef}
                    type="text"
                    placeholder="Código de barras..."
                    value={quickStockData.barcode}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Evitar envío inmediato por el escáner
                        if (quickStockData.product) {
                          // Si el producto existe, mover foco al siguiente campo (stockToAdd)
                          const next = document.getElementById('quick-stock-add');
                          if (next) next.focus();
                        }
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuickStockData({ ...quickStockData, barcode: val });
                      handleQuickStockSearch(val);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: 10,
                      border: '2px solid #e2e8f0',
                      fontSize: 16,
                      transition: 'all 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {quickStockData.product ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    style={{ background: '#ebf8ff', padding: 16, borderRadius: 12, border: '1px solid #bee3f8' }}
                  >
                    <div style={{ fontWeight: 800, color: '#2b6cb0', fontSize: 15, marginBottom: 4 }}>
                      {quickStockData.product.name}
                    </div>
                    <div style={{ fontSize: 13, color: '#4a5568', marginBottom: 12 }}>
                      Stock actual: <strong style={{ color: '#2d3748' }}>{quickStockData.product.stock}</strong> ·
                      Costo: <strong style={{ color: '#2d3748' }}>${quickStockData.product.costPrice.toFixed(2)}</strong>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#2c5282', display: 'block', marginBottom: 4 }}>Sumar Stock</label>
                        <input
                          id="quick-stock-add"
                          type="number"
                          placeholder="+ Cantidad"
                          value={quickStockData.stockToAdd}
                          onChange={(e) => setQuickStockData({ ...quickStockData, stockToAdd: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e0',
                            fontSize: 15
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#2c5282', display: 'block', marginBottom: 4 }}>Nuevo Costo</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder={`$${quickStockData.product.costPrice}`}
                          value={quickStockData.costPrice}
                          onChange={(e) => {
                            setQuickStockData({
                              ...quickStockData,
                              costPrice: e.target.value
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e0',
                            fontSize: 15
                          }}
                        />
                      </div>
                    </div>


                  </motion.div>
                ) : quickStockData.barcode && (
                  <div style={{ fontSize: 13, color: '#e53e3e', background: '#fff5f5', padding: '10px', borderRadius: 8, textAlign: 'center' }}>
                    Producto no encontrado
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button
                    type="submit"
                    disabled={!quickStockData.product}
                    style={{
                      flex: 2,
                      background: '#3182ce',
                      color: 'white',
                      border: 'none',
                      padding: '14px',
                      borderRadius: 12,
                      fontWeight: 800,
                      cursor: quickStockData.product ? 'pointer' : 'not-allowed',
                      opacity: quickStockData.product ? 1 : 0.5,
                      boxShadow: '0 4px 6px rgba(49,130,206,0.3)',
                      transition: 'all 0.2s'
                    }}
                  >
                    Actualizar (Enter)
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowQuickStockModal(false); focusSearch(); }}
                    style={{
                      flex: 1,
                      background: '#edf2f7',
                      color: '#4a5568',
                      border: 'none',
                      padding: '14px',
                      borderRadius: 12,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Esc
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modal Búsqueda Externa (Mercado Libre) */}
      <AnimatePresence>
        {showSearchExternalModal && (
          <motion.div style={modalBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ ...modalContent, width: isMobile ? '95%' : '800px', maxWidth: '98%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0 }}>🔍 Buscador Web de Imágenes</h2>
                <button type="button" onClick={() => setShowSearchExternalModal(false)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <input
                  type="text"
                  placeholder="Ej: Auriculares Sony, Coca Cola 1.5L..."
                  value={searchExternalQuery}
                  onChange={e => setSearchExternalQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearchExternal()}
                  style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #ddd' }}
                />
                <button onClick={handleSearchExternal} disabled={isSearchingExternal} style={{ ...btnGreen, padding: '0 25px' }}>
                  {isSearchingExternal ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 5 }}>
                {isSearchingExternal ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>Cargando sugerencias...</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    {Array.isArray(externalResults) && externalResults.map((item) => (
                      <div key={item.id} style={{ display: 'flex', gap: 12, padding: 10, border: '1px solid #eee', borderRadius: 8, cursor: 'pointer', transition: 'background 0.2s', position: 'relative' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f7f7f7'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        onClick={async () => {
                          try {
                            const loadingToast = toast.loading('Descargando imagen externa...');
                            const response = await http.post('/product/download-external-image', { url: item.thumbnail });
                            const newImage = response.data.imageUrl;
                            
                            // Guardar en frontend
                            const updatedImages = [...(formData.ecommerceImages || []), newImage];
                            setFormData(p => ({ ...p, ecommerceImages: updatedImages }));
                            
                            // Guardar en backend (Auto-save) así impacta en la tienda al instante
                            if (formData._id) {
                                await http.patch(ENDPOINTS.update, {
                                    _id: formData._id,
                                    ecommerceImages: updatedImages
                                });
                            }
                            
                            toast.update(loadingToast, { render: '¡Imagen adjuntada al producto!', type: 'success', isLoading: false, autoClose: 3000 });
                          } catch (err) {
                            console.error('Error al descargar la imagen:', err);
                            toast.dismiss();
                            toast.error('Error al guardar la imagen');
                          }
                        }}>
                        <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff', borderRadius: 4, cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(item.thumbnail);
                              setShowPreviewLightbox(true);
                            }}
                            title="Haz clic para ver en grande"
                          />
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(item.thumbnail);
                              setShowPreviewLightbox(true);
                            }}
                            style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 4px', borderRadius: 3, fontSize: 8, cursor: 'pointer' }}
                          >
                            放大
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.2em', height: '2.4em' }} title={item.title}>
                            {item.title}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 4 }}>
                            <div style={{ color: item.price > 0 ? '#28a745' : '#666', fontWeight: 'bold', fontSize: 14 }}>
                              {item.price > 0 ? `$${item.price.toLocaleString()}` : '-'}
                            </div>
                            <span style={{ fontSize: 9, background: '#edf2f7', color: '#4a5568', padding: '2px 5px', borderRadius: 4, fontWeight: 'bold', textTransform: 'uppercase' }}>
                              {item.source || 'Web'}
                            </span>
                          </div>
                          <button style={{ marginTop: 6, background: '#007bff', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, width: '100%' }}>Usar Imagen</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!isSearchingExternal && (externalResults || []).length === 0 && searchExternalQuery && (
                <p style={{ textAlign: 'center', color: '#666', marginTop: 20 }}>No se encontraron resultados para "{searchExternalQuery}"</p>
              )}

              <div style={{ marginTop: 20, textAlign: 'right' }}>
                <button type="button" onClick={() => setShowSearchExternalModal(false)} style={btnNav}>Cerrar</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox para previsualización dentro de administración */}
      <AnimatePresence>
        {showPreviewLightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            onClick={() => setShowPreviewLightbox(false)}
          >
            <button style={{ position: 'absolute', top: 20, right: 25, background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '30px', cursor: 'pointer', fontSize: '16px' }}>
              ✕ Cerrar
            </button>
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={previewImage}
              alt="Previsualización"
              style={{ maxWidth: '95%', maxHeight: '95vh', objectFit: 'contain', boxShadow: '0 0 30px rgba(0,0,0,0.5)' }}
              onClick={(e) => e.stopPropagation()}
            />
            <div style={{ position: 'absolute', bottom: 40, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '15px 30px', borderRadius: '50px', fontSize: '16px', fontWeight: 'bold' }}>
              Vista en Alta Resolución
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- MODAL ESTACIÓN DE FOTOS (FAST MODE) --- */}
      <AnimatePresence>
        {showPhotoMode && (
          <motion.div
            style={modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              style={{
                ...modalContent,
                width: isMobile ? '95%' : '900px',
                maxWidth: '1200px',
                height: isMobile ? '90vh' : '80vh',
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                padding: '25px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h2 style={{ margin: 0 }}>📸 Estación de Fotos</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ background: '#e9ecef', padding: '5px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
                    Pendientes: {pendingPhotosList.length}
                  </span>
                  <button
                    onClick={() => setShowPhotoMode(false)}
                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '25px', flex: 1, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
                {/* LADO IZQUIERDO: Escaneo y Captura */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px', overflowY: 'auto', paddingRight: '5px' }}>
                  <label style={{ display: 'block', fontSize: '14px', color: '#495057', fontWeight: 'bold' }}>
                    Escanea o Busca:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      ref={photoModeInputRef}
                      type="text"
                      value={photoModeBarcode}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPhotoModeBarcode(val);
                        handlePhotoModeSearch(val);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '12px',
                        fontSize: '18px',
                        borderRadius: '8px',
                        border: '2px solid #007bff',
                        textAlign: 'center'
                      }}
                      placeholder="Eje: 7791234567890"
                    />
                    <button
                      onClick={() => setIsScanningPhotoCode(!isScanningPhotoCode)}
                      style={{
                        padding: '0 20px',
                        background: isScanningPhotoCode ? '#dc3545' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '20px',
                        cursor: 'pointer'
                      }}
                    >
                      {isScanningPhotoCode ? '✕' : '📷'}
                    </button>
                  </div>

                  {isScanningPhotoCode && (
                    <div style={{ borderRadius: '10px', overflow: 'hidden', border: '2px solid #007bff' }}>
                      <BarcodeScannerHtml5Qrcode onDetected={onScanDetected} />
                    </div>
                  )}

                  {photoModeProduct ? (
                    <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e1e4e8' }}>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: '#212529', textAlign: 'center' }}>
                        {photoModeProduct.name}
                      </div>

                      <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', marginBottom: '20px', paddingBottom: '10px' }}>
                        {[photoModeProduct.image, ...(photoModeProduct.ecommerceImages || [])].filter(Boolean).filter(img => !img.startsWith('http')).length > 0 ? 
                          [photoModeProduct.image, ...(photoModeProduct.ecommerceImages || [])].filter(Boolean).filter(img => !img.startsWith('http')).map((img, i) => (
                          <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                            <img src={resolveImageUrl(img, 200)} style={{ height: '100px', borderRadius: '6px', border: '1px solid #eee' }} alt="actual" />
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeletePhotoFromMode(i); }}
                              style={{
                                position: 'absolute', top: '-8px', right: '-8px',
                                background: '#dc3545', color: 'white', border: 'none', borderRadius: '50%',
                                width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                fontSize: '14px', zIndex: 2
                              }}
                            >✕</button>
                          </div>
                        )) : (
                          <div style={{ width: '100%', height: '80px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', color: '#999' }}>
                            Sin fotos locales
                          </div>
                        )}
                      </div>

                      <button
                        disabled={photoModeLoading}
                        onClick={() => photoModeFileRef.current.click()}
                        style={{
                          width: '100%', padding: '15px', background: '#28a745', color: 'white', border: 'none',
                          borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
                        }}
                      >
                        {photoModeLoading ? '⏳ Procesando...' : '📸 TOMAR FOTO'}
                      </button>

                      <input ref={photoModeFileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoModeUpload} />
                      
                      <button
                        onClick={() => {
                          setPhotoModeBarcode('');
                          setPhotoModeProduct(null);
                          if (photoModeInputRef.current) photoModeInputRef.current.focus();
                        }}
                        style={{ width: '100%', padding: '12px', marginTop: '10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        SIGUIENTE (Limpiar)
                      </button>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adb5bd', border: '2px dashed #dee2e6', borderRadius: '10px' }}>
                      {photoModeBarcode ? '❌ Producto no encontrado' : '🔍 Escanea o elige de la lista'}
                    </div>
                  )}
                </div>

                {/* LADO DERECHO: Lista Pendientes */}
                {!isMobile && (
                  <div style={{ width: '320px', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '10px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
                    <div style={{ padding: '12px', background: '#e9ecef', fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>
                      🗒️ Pendientes de Foto ({pendingPhotosList.length})
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '5px' }}>
                      {pendingPhotosList.length > 0 ? pendingPhotosList.map(p => (
                        <div
                          key={p._id}
                          onClick={() => {
                            setPhotoModeProduct(p);
                            setPhotoModeBarcode(p.barcode);
                            if (photoModeInputRef.current) photoModeInputRef.current.focus();
                          }}
                          style={{
                            padding: '10px',
                            borderBottom: '1px solid #f1f3f5',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'background 0.2s'
                          }}
                          className="hover-bg-light"
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ fontWeight: 'bold', color: '#343a40' }}>{p.name}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                            <code style={{ fontSize: '11px', color: '#007bff' }}>{p.barcode}</code>
                            <span style={{ fontSize: '11px', color: '#6c757d' }}>{p.category}</span>
                          </div>
                        </div>
                      )) : (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#28a745' }}>
                          🎊 ¡Catálogo completo!
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div >
  );
};

// 🔹 Estilos
const btnNav = { background: '#007bff', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const btnGreen = { background: '#28a745', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const btnRed = { background: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
// Small variant for inline actions (e.g. remove component) keep visual consistency with primary red
// (btnSmall / btnSmallRed removed from Products — use global small buttons from Sales when needed)
const inputSearch = { padding: '8px', borderRadius: '6px', border: '1px solid #ccc', width: 'min(520px, 100%)' };
const table = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' };
const th = { padding: '10px', borderBottom: '2px solid #ccc', textAlign: 'left' };
const td = { padding: '10px', borderBottom: '1px solid #eee' };
const modalBackdrop = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50 };
const modalContent = { backgroundColor: '#fff', borderRadius: '10px', padding: '20px', width: '450px', boxShadow: '0 0 15px rgba(0,0,0,0.2)' };
const modalOverlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 60 };

// Preview component used inside the bulk-adjust modal
function PreviewBulk({ products = [], category = '', pctCost = 0, pctMargin = 0, pctSale = 0 }) {
  const candidates = (products || []).filter(p => {
    if (!p) return false;
    if (category && String(category || '').trim() !== '') {
      return String(p.category || '').toLowerCase() === String(category || '').toLowerCase();
    }
    return true;
  }).slice(0, 6);

  if (!candidates || candidates.length === 0) return (<div style={{ color: '#666' }}>No hay productos para previsualizar.</div>);

  return (
    <div style={{ border: '1px solid #eee', padding: 8, borderRadius: 6, marginTop: 8 }}>
      <div style={{ fontSize: 13, marginBottom: 6, color: '#333' }}>Mostrando {candidates.length} ejemplo(s):</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 6 }}>Nombre</th>
            <th style={{ textAlign: 'right', padding: 6 }}>Costo →</th>
            <th style={{ textAlign: 'right', padding: 6 }}>Margen →</th>
            <th style={{ textAlign: 'right', padding: 6 }}>Precio →</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((p) => {
            // Apply same dependency rules as the runner: cost/margin -> sale, sale -> margin
            const oldCost = Number(p.costPrice || 0);
            const oldMargin = Number(p.margin || 0);
            const oldSale = Number(p.salePrice || 0);
            const pctC = Number(pctCost || 0);
            const pctM = Number(pctMargin || 0);
            const pctS = Number(pctSale || 0);

            const nc = round2(oldCost * (1 + pctC / 100));
            let nm = round2(oldMargin * (1 + pctM / 100));
            let ns;
            if (pctS && Math.abs(pctS) > 1e-9) {
              ns = round2(oldSale * (1 + pctS / 100));
              if (nc > 0) {
                const computed = ((ns - nc) / nc) * 100;
                nm = round2(isFinite(computed) ? computed : nm);
              }
            } else {
              ns = round2(nc * (1 + nm / 100));
            }
            return (
              <tr key={p._id} style={{ borderTop: '1px solid #f3f3f3' }}>
                <td style={{ padding: 6 }}>{p.name}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>${Number(p.costPrice || 0).toFixed(2)} → <strong>${nc.toFixed(2)}</strong></td>
                <td style={{ padding: 6, textAlign: 'right' }}>{Number(p.margin || 0).toFixed(2)}% → <strong>{nm.toFixed(2)}%</strong></td>
                <td style={{ padding: 6, textAlign: 'right' }}>${Number(p.salePrice || 0).toFixed(2)} → <strong>${ns.toFixed(2)}</strong></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(products || []).length > candidates.length && (<div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Mostrando sólo primeros {candidates.length} productos como ejemplo.</div>)}
    </div>
  );
}

export default Products;
